import React, { useState, useEffect } from 'react';
import { X, Trash2, ShoppingCart, ChevronRight, Plus, Truck, MapPin, Zap, Share2, Check, AlertCircle, Sparkles, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SurveyModal from './SurveyModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_DELIVERY_CITIES = ['RIO TERCERO', 'HERNANDO', 'TANCACHA', 'VILLA ASCASUBI', 'ALMAFUERTE'];

const CartSidebar = () => {
  const { 
    cart, updateQuantity, removeFromCart, getCartTotal, formatPrice, 
    isCartOpen, setIsCartOpen, clearCart, getItemPrice, addToCart,
    shippingData, saveShippingData, isRetail, storeSettings,
    meetsMinimumPurchase, getRawCartTotal, getsWholesalePrices, getPriceForQuantity,
    getApplicableVolumeTier, getVolumeDiscountAmount,
    getProfileBonusAmount, profileBonus, isProfileComplete,
    getGiftItems, getGiftSavings,
    priceUpdate, dismissPriceUpdate,
    setItemManualDiscount, setSelectedPaymentMethod,
    setItemBranch,
  } = useCart();
  
  const { theme } = useTheme();
  const { user, getEffectiveCustomerType, getBestCartDiscountPct, getBestDiscountPct, getNextCartDiscountTier } = useAuth();
  const navigate = useNavigate();
  
  const [showCheckout, setShowCheckout] = useState(false);
  const [formData, setFormData] = useState(shippingData);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [coupon, setCoupon] = useState({ code: '', applied: null, error: '' });
  const [suggestions, setSuggestions] = useState([]);
  const [confirmClear, setConfirmClear] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [sellerClients, setSellerClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientList, setShowClientList] = useState(false);
  // 📊 Progreso hacia el próximo objetivo de descuento. Se muestra siempre
  // arriba de los botones "Finalizar Compra" y "Enviar Pedido por WhatsApp"
  // para que el cliente sepa cuánto le falta para el siguiente escalón, sin
  // esperar al modal ni al submit.
  const [orderAsVendedor, setOrderAsVendedor] = useState(false);
  const [savedCommerceData, setSavedCommerceData] = useState(null);
  // Encuesta gate: si hay encuesta pendiente al apretar "Finalizar Pedido",
  // la mostramos PRIMERO. Recién al responder/skipear continuamos.
  const [pendingSurveyGate, setPendingSurveyGate] = useState(null);
  const [surveyChecked, setSurveyChecked] = useState(false);
  const [shippingSettings, setShippingSettings] = useState({
    pickupEnabled: true, fastShippingEnabled: true, fastShippingCost: 20000,
    deliveryEnabled: true, deliveryCities: DEFAULT_DELIVERY_CITIES,
    whatsappNumber: '543571522400', customMethods: [], deliverySchedules: []
  });

  const isSellerMode = user?.role === 'seller' || (user?.role === 'admin' && orderAsVendedor);
  const [sellerProfile, setSellerProfile] = useState(null);

  useEffect(() => { fetchShippingSettings(); }, []);
  useEffect(() => { if (isCartOpen && cart.length > 0) fetchSuggestions(); }, [isCartOpen, cart]);
  useEffect(() => { setFormData(shippingData); }, [shippingData]);
  // Autocompletar el formulario con los datos del perfil del usuario.
  // Si el usuario es comercio + ya cargó CUIT y condición IVA, no hace falta
  // que los pida en cada checkout. Pre-rellenamos sin pisar lo que ya escribió.
  useEffect(() => {
    if (!user) return;
    setFormData(prev => ({
      ...prev,
      customerName: prev.customerName || user.name || user.firstName || '',
      businessName: prev.businessName || user.businessName || '',
      phone: prev.phone || user.phone || '',
      address: prev.address || user.address || '',
      city: prev.city || user.city || '',
      cuit: prev.cuit || user.cuit || '',
      // Si tiene CUIT cargado en perfil, sugerir transferencia (usar Factura A)
      paymentMethod: prev.paymentMethod || (user.cuit ? 'transferencia' : 'efectivo'),
      requiresInvoice: prev.requiresInvoice || (user.customer_type === 'comercio' || user.customerType === 'comercio'),
    }));
  }, [user]);
  useEffect(() => {
    if ((user?.role === 'seller' || user?.role === 'admin') && isCartOpen) {
      fetchSellerClients();
      axios.get(`${API}/seller/profile`, { withCredentials: true })
        .then(r => setSellerProfile(r.data))
        .catch(() => {});
    }
  }, [user, isCartOpen]);
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/user/points`, { withCredentials: true })
      .then(r => { if (r.data?.referral_code) setReferralCode(r.data.referral_code); })
      .catch(() => {});
  }, [user]);

  // Si el método de pago elegido tiene forcesPickup, forzar shippingMethod=pickup.
  // Priorizamos la lista de la audiencia activa (B2C consumer, B2B default)
  // para que .find(key) no matchee la entrada de la otra audiencia y termine
  // aplicando un adjustPercent distinto al que se usa en getFinalTotal.
  const _isConsumerFinalPM = getEffectiveCustomerType?.() === 'consumidor_final';
  const _allPaymentMethods = _isConsumerFinalPM
    ? [
        ...(storeSettings?.consumerPaymentMethods || []),
        ...(storeSettings?.paymentMethods || []),
      ]
    : [
        ...(storeSettings?.paymentMethods || []),
        ...(storeSettings?.consumerPaymentMethods || []),
      ];
  const _selectedPaymentMethod = _allPaymentMethods.find(m => m.key === formData.paymentMethod);
  const _forcesPickup = _selectedPaymentMethod?.forcesPickup === true;
  const _blocksVolumeDiscounts = _selectedPaymentMethod?.allowVolumeDiscounts === false;
  useEffect(() => {
    if (_forcesPickup && formData.shippingMethod !== 'pickup') {
      setFormData(prev => ({ ...prev, shippingMethod: 'pickup' }));
    }
  }, [_forcesPickup, formData.shippingMethod]);
  // Sincronizar el método seleccionado con el CartContext para que el
  // catálogo (ProductCard/FeaturedCard) reaccione al flag allowVolumeDiscounts.
  useEffect(() => {
    if (setSelectedPaymentMethod) setSelectedPaymentMethod(_selectedPaymentMethod || null);
  }, [_selectedPaymentMethod, setSelectedPaymentMethod]);

  const fetchSellerClients = async () => {
    try {
      const res = await axios.get(`${API}/seller/clients`, { withCredentials: true });
      setSellerClients(res.data || []);
    } catch (e) { console.error('Error fetching seller clients:', e); }
  };

  const [clientHistory, setClientHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const selectSellerClient = async (client) => {
    setFormData(prev => ({
      ...prev,
      sellerClientName: client.name || '',
      sellerClientBusiness: client.businessName || '',
      sellerClientPhone: client.phone || '',
      sellerClientCity: client.city || '',
      sellerClientAddress: client.address || '',
      customerName: client.name || prev.customerName,
      businessName: client.businessName || prev.businessName,
      address: client.address || prev.address,
      city: client.city || prev.city,
      phone: client.phone || prev.phone
    }));
    setClientSearch('');
    setShowClientList(false);
    
    // Fetch client purchase history
    const searchName = client.name || client.businessName || '';
    if (searchName) {
      setLoadingHistory(true);
      try {
        const res = await axios.get(`${API}/seller/client-history/${encodeURIComponent(searchName)}`, { withCredentials: true });
        setClientHistory(res.data);
      } catch (e) { setClientHistory(null); }
      finally { setLoadingHistory(false); }
    }
  };

  const fetchShippingSettings = async () => {
    try {
      const response = await axios.get(`${API}/shipping-settings`, {
        headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' },
        params: { _ts: Date.now() },
      });
      setShippingSettings(response.data);
    } catch (error) { console.error('Error fetching shipping settings:', error); }
  };

  const fetchSuggestions = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      const allProducts = response.data.filter(p => p.activo);
      const cartIds = cart.map(item => item.id);
      const available = allProducts.filter(p => !cartIds.includes(p.id));
      const cartGroups = [...new Set(cart.map(item => item.grupo).filter(Boolean))];
      const scored = available.map(p => ({
        ...p,
        score: (p.destacado ? 10 : 0) + (p.oferta ? 8 : 0) + (cartGroups.includes(p.grupo) ? 5 : 0)
      }));
      scored.sort((a, b) => b.score - a.score);
      setSuggestions(scored.slice(0, 10));
    } catch (error) { console.error('Error fetching suggestions:', error); }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      // 💾 Persistir el cambio de forma de pago / método de envío en el
      // mismo momento para que sobreviva a los reloads y no se pierda por
      // AppGate. Antes sólo se persistía al enviar el pedido.
      if (name === 'paymentMethod' || name === 'shippingMethod') {
        try { saveShippingData(next); } catch { /* ignore */ }
      }
      return next;
    });
  };

  // 🎟️ Un cupón de reactivación es ÚNICO: reemplaza TODOS los demás descuentos
  // (objetivo, volumen, perfil, ajuste por forma de pago). Se aplica sobre el
  // precio de LISTA base de cada item.
  const couponIsSingle = () => !!(coupon.applied && coupon.applied.singleDiscount);
  const getListSubtotal = () => cart.reduce((s, it) => s + (Number(it.priceNumeric) || 0) * (it.quantity || 0), 0);

  const getCouponDiscount = () => {
    if (!coupon.applied) return 0;
    if (couponIsSingle()) {
      const listSub = getListSubtotal();
      if (listSub < (coupon.applied.minTotal || 0)) return 0;
      return Math.round(listSub * (Number(coupon.applied.discountPercent) || 0) / 100 * 100) / 100;
    }
    const subtotal = getCartTotal() - (getVolumeDiscountAmount?.() || 0) - (getProfileBonusAmount?.(user) || 0);
    if (subtotal < (coupon.applied.minTotal || 0)) return 0;
    return Math.round(subtotal * (Number(coupon.applied.discountPercent) || 0) / 100 * 100) / 100;
  };

  const applyCoupon = async () => {
    const code = (coupon.code || '').trim().toUpperCase();
    if (!code) return;
    try {
      const r = await axios.get(`${API}/coupons/${code}/check`);
      setCoupon({ code, applied: r.data, error: '' });
    } catch (e) {
      setCoupon(c => ({ ...c, applied: null, error: e.response?.data?.detail || 'Cupón inválido' }));
    }
  };

  // ⚖️ Regla: combos y items con descuento por cantidad NO deben recibir
  // ajuste por forma de pago (no se apilan los descuentos).
  const itemHasOwnDiscount = (item) => {
    if (!item) return false;
    if (item.isComboItem) return true;
    if (!item.quantityPrices || item.quantityPrices.length === 0) return false;
    const tierPrice = getPriceForQuantity(item, item.quantity, false);
    const basePrice = item.priceNumeric || 0;
    return tierPrice && basePrice && tierPrice < basePrice - 0.01;
  };

  const getPaymentAdjustment = () => {
    if (couponIsSingle()) return 0;  // cupón único: sin ajuste por forma de pago
    const isConsumerFinal = getEffectiveCustomerType?.() === 'consumidor_final';
    const pmList = (isConsumerFinal
      ? (storeSettings?.consumerPaymentMethods || storeSettings?.paymentMethods)
      : storeSettings?.paymentMethods) || [];
    const pm = pmList.find(m => m.key === formData.paymentMethod);
    if (!pm || !pm.adjustPercent) return 0;
    // Sólo aplica al subtotal de items SIN descuento propio (combo/volumen).
    const eligibleSubtotal = cart.reduce((sum, item) => {
      if (itemHasOwnDiscount(item)) return sum;
      return sum + getItemPrice(item) * (item.quantity || 0);
    }, 0);
    const base = Math.max(0, eligibleSubtotal - (getVolumeDiscountAmount?.() || 0) - (getProfileBonusAmount?.(user) || 0) - getCouponDiscount());
    // Regla del cliente: SIEMPRE redondear hacia arriba al peso (Math.ceil)
    const raw = base * (pm.adjustPercent / 100);
    return raw < 0 ? -Math.ceil(Math.abs(raw)) : Math.ceil(raw);
  };

  const getFinalTotal = () => {
    // 🎟️ Cupón ÚNICO: reemplaza TODOS los demás descuentos. Total = lista × (1-%).
    if (couponIsSingle()) {
      let total = getListSubtotal() - getCouponDiscount();
      if (formData.shippingMethod === 'fast') {
        total += shippingSettings.fastShippingCost;
      } else if (formData.shippingMethod?.startsWith('custom_')) {
        const customMethod = shippingSettings.customMethods?.find(m => `custom_${m.id}` === formData.shippingMethod);
        if (customMethod) total += customMethod.cost || 0;
      }
      return Math.max(0, total);
    }
    // 🧮 Total = suma de cada item con su ajuste por forma de pago aplicado
    // sólo si NO tiene descuento propio (combo / volumen). Items con combo o
    // tier activo se cobran a su precio fijo, sin sumar/restar pmAdj.
    const isConsumerFinal = getEffectiveCustomerType?.() === 'consumidor_final';
    const pmList = (isConsumerFinal
      ? (storeSettings?.consumerPaymentMethods || storeSettings?.paymentMethods)
      : storeSettings?.paymentMethods) || [];
    const pm = pmList.find(m => m.key === formData.paymentMethod);
    const pmAdj = Number(pm?.adjustPercent || 0);
    const applyPm = (item, p) => {
      if (pmAdj === 0 || itemHasOwnDiscount(item)) return p;
      return Math.ceil(p * (1 + pmAdj / 100));
    };

    // Subtotal de items con ajuste aplicado item-a-item
    let total = cart.reduce((sum, item) => {
      const unit = applyPm(item, getItemPrice(item));
      return sum + unit * (item.quantity || 0);
    }, 0);

    // Descuentos NO relacionados con forma de pago (volumen, cupón, perfil)
    total -= (getVolumeDiscountAmount?.() || 0);
    total -= (getProfileBonusAmount?.(user) || 0);
    total -= getCouponDiscount();
    if (formData.shippingMethod === 'fast') {
      total += shippingSettings.fastShippingCost;
    } else if (formData.shippingMethod?.startsWith('custom_')) {
      const customMethod = shippingSettings.customMethods?.find(m => `custom_${m.id}` === formData.shippingMethod);
      if (customMethod) total += customMethod.cost || 0;
    }
    return Math.max(0, total);
  };
  
  const getShippingCost = () => {
    if (formData.shippingMethod === 'fast') return shippingSettings.fastShippingCost;
    if (formData.shippingMethod?.startsWith('custom_')) {
      const customMethod = shippingSettings.customMethods?.find(m => `custom_${m.id}` === formData.shippingMethod);
      return customMethod?.cost || 0;
    }
    return 0;
  };
  
  const normalizeCity = (s) => (s || '').toUpperCase().trim()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // quita acentos
    .replace(/[^A-Z0-9 ]/g, ''); // solo letras y espacios
  
  const isDeliveryAvailable = () => {
    const city = isSellerMode ? (formData.sellerClientCity || formData.city) : formData.city;
    if (!city) return false;
    const userCity = normalizeCity(city);
    return shippingSettings.deliveryCities.some(c => {
      const nc = normalizeCity(c);
      return userCity.includes(nc) || nc.includes(userCity);
    });
  };

  // 🎯 Mensaje SIEMPRE VISIBLE arriba del botón de finalizar/enviar pedido.
  // Muestra: "Te faltan $X para llegar al descuento del Y%" (o "Máximo
  // descuento aplicado" si ya está en el tope). Se actualiza en tiempo real
  // porque leemos `getRawCartTotal()` en cada render.
  const renderNextTierHint = (variant = 'cart') => {
    const raw = Number((typeof getRawCartTotal === 'function' ? getRawCartTotal() : 0) || 0);
    if (raw <= 0) return null;
    const nxt = typeof getNextCartDiscountTier === 'function' ? getNextCartDiscountTier(raw) : null;
    const bestPct = typeof getBestCartDiscountPct === 'function' ? Number(getBestCartDiscountPct(raw) || 0) : 0;

    if (nxt && Number(nxt.threshold) > 0 && Number(nxt.missing) > 0) {
      return (
        <div
          className="rounded-xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 px-3 py-2.5 flex items-start gap-2 shadow-sm"
          data-testid={`next-tier-hint-${variant}`}
        >
          <TrendingUp size={18} className="text-orange-600 flex-shrink-0 mt-0.5" />
          <p className="text-[13px] leading-snug text-slate-800">
            Te faltan{' '}
            <strong className="text-orange-700 tabular-nums" data-testid={`next-tier-hint-missing-${variant}`}>
              {formatPrice(Number(nxt.missing))}
            </strong>{' '}
            para llegar al descuento del{' '}
            <strong className="text-orange-700" data-testid={`next-tier-hint-pct-${variant}`}>
              {Number(nxt.pct)}%
            </strong>
          </p>
        </div>
      );
    }
    if (bestPct > 0) {
      return (
        <div
          className="rounded-xl border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50 px-3 py-2.5 flex items-start gap-2 shadow-sm"
          data-testid={`next-tier-hint-max-${variant}`}
        >
          <Sparkles size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
          <p className="text-[13px] leading-snug text-slate-800">
            ¡Ya alcanzaste el <strong className="text-emerald-700">máximo descuento del {bestPct}%</strong> en tu pedido!
          </p>
        </div>
      );
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!formData.shippingMethod) {
      setSubmitError('Por favor seleccioná un método de envío.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // 🛑 Validación dura del mínimo de compra retail (consumidor final).
    if (isRetail && !meetsMinimumPurchase()) {
      const min = storeSettings?.retailMinimumPurchase || 0;
      const cur = getCartTotal();
      const falta = Math.max(0, min - cur);
      setSubmitError(`Te faltan ${formatPrice(falta)} para alcanzar el pedido mínimo de ${formatPrice(min)}. Seguí agregando productos para continuar.`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Validación CUIT cuando el pago es por Transferencia.
    // Sólo aplica a COMERCIO (factura A). Consumidor Final lleva Factura B,
    // no requiere CUIT.
    if (formData.paymentMethod === 'transferencia' && getEffectiveCustomerType?.() === 'comercio') {
      const digits = (formData.cuit || '').replace(/\D/g, '');
      if (digits.length !== 11) {
        setSubmitError('Ingresá un CUIT/CUIL válido de 11 dígitos. Es obligatorio para emitir la factura A.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    // 💰 Validación de compatibilidad tier ↔ forma de pago: si el carrito tiene
    // ítems con descuento por cantidad activo, solo métodos con
    // `acceptsBulkPricing=true` pueden usarse.
    {
      const pmSel = _allPaymentMethods.find(p => p.key === formData.paymentMethod);
      const cartHasTier = cart.some(itemHasOwnDiscount);
      if (cartHasTier && pmSel && pmSel.acceptsBulkPricing !== true) {
        setSubmitError('Tenés productos con descuento por cantidad en el carrito. Esos precios son EXCLUSIVOS para pagos en efectivo. Cambiá la forma de pago a Efectivo o quitá los productos con descuento por cantidad.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    // 🎯 La alerta de "Estás cerca del próximo descuento" ahora es un mensaje
    // siempre visible arriba del botón (ver `NextTierHint` más abajo), así que
    // acá no interrumpimos el submit. El cliente decide con la información
    // frente a él.

    // Backup por si el radio deshabilitado se salteó por autofill/carga previa.
    {
      const pmSel = _allPaymentMethods.find(p => p.key === formData.paymentMethod);
      const minAmt = Number(pmSel?.minAmount || 0);
      const subtotal = Number(getRawCartTotal?.() || 0);
      if (minAmt > 0 && subtotal < minAmt) {
        const missing = minAmt - subtotal;
        setSubmitError(`La forma de pago "${pmSel?.label || formData.paymentMethod}" requiere una compra mínima de ${formatPrice(minAmt)}. Te faltan ${formatPrice(missing)}. Sumá productos o elegí otro método.`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    // 🏪 Validación de asignación de sucursal (solo B2B con al menos una sucursal
    // guardada). Si el comercio tiene sucursales configuradas, todos los items
    // regulares deben tener una asignada antes de enviar el pedido. Los combos
    // y los regalos quedan exentos.
    {
      const isB2B = getEffectiveCustomerType?.() === 'comercio';
      const savedBranches = Array.isArray(user?.branches) ? user.branches : [];
      if (isB2B && savedBranches.length > 0) {
        const unassigned = cart.filter(it => !it.isGift && !it.isComboItem && !((it.branchName || '').trim()));
        if (unassigned.length > 0) {
          const preview = unassigned.slice(0, 3).map(it => `• ${it.name}`).join('\n');
          const extra = unassigned.length > 3 ? `\n…y ${unassigned.length - 3} más` : '';
          setSubmitError(`Tenés ${unassigned.length} producto(s) sin sucursal asignada. Elegí una sucursal para cada uno antes de enviar el pedido:\n${preview}${extra}`);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
      }
    }

    // 🎯 GATE de encuesta: si hay una encuesta activa pendiente, mostrarla
    // ANTES de crear el pedido y abrir WhatsApp. Recién al responder/skipear
    // continuamos con el flujo de envío.
    if (user && !surveyChecked) {
      try {
        const r = await axios.get(`${API}/surveys/pending`);
        if (r.data?.survey) {
          setPendingSurveyGate(r.data.survey);
          setSurveyChecked(true);
          return; // Pausa: esperamos a que onSurveyClose se ejecute
        }
      } catch { /* si falla, continuamos sin bloquear el pedido */ }
      setSurveyChecked(true);
    }

    setLoading(true);
    try {
      saveShippingData(formData);
      let shippingMethodName = 'Reparto gratuito';
      if (formData.shippingMethod === 'pickup') shippingMethodName = 'Retiro en depósito';
      else if (formData.shippingMethod === 'fast') shippingMethodName = `Envío rápido (+${formatPrice(shippingSettings.fastShippingCost)})`;
      else if (formData.shippingMethod?.startsWith('custom_')) {
        const customMethod = shippingSettings.customMethods?.find(m => `custom_${m.id}` === formData.shippingMethod);
        if (customMethod) shippingMethodName = customMethod.cost > 0 ? `${customMethod.name} (+${formatPrice(customMethod.cost)})` : customMethod.name;
      }
      // 💰 Aplicar el ajuste por forma de pago (descuento/recargo) AL PRECIO
      // UNITARIO de cada producto. Así el WhatsApp del pedido y la factura
      // muestran el precio final por item, no un precio de lista + descuento
      // aparte. Redondeo hacia arriba al peso.
      const _pmSel = _allPaymentMethods.find(p => p.key === formData.paymentMethod);
      const _pmAdj = Number(_pmSel?.adjustPercent || 0);
      // Items con descuento propio (combo/volumen) NO reciben ajuste por forma de pago.
      const applyPm = (item, price) => {
        if (_pmAdj === 0 || itemHasOwnDiscount(item)) return price;
        return Math.ceil(price * (1 + _pmAdj / 100));
      };

      const orderItems = cart.map(item => ({
        productId: item.isGift ? (item.productId || item.id) : (item.productId || item.id),
        productName: item.name,
        quantity: item.quantity,
        unitPrice: item.isGift ? 0 : applyPm(item, getItemPrice(item)),
        totalPrice: item.isGift ? 0 : applyPm(item, getItemPrice(item)) * item.quantity,
        listUnitPrice: item.isGift ? 0 : getItemPrice(item),  // referencia interna (precio de lista)
        ...(item.branchName ? { branchName: item.branchName } : {}),
        ...(item.isGift ? { isGift: true, giftTierThreshold: item.giftTierThreshold } : {}),
      }));
      // For seller mode, populate main fields from seller client data
      const submitFormData = { ...formData };
      if (isSellerMode) {
        submitFormData.customerName = formData.sellerClientName || formData.customerName || '';
        submitFormData.businessName = formData.sellerClientBusiness || formData.businessName || '';
        submitFormData.phone = formData.sellerClientPhone || formData.phone || '';
        submitFormData.city = formData.sellerClientCity || formData.city || '';
        submitFormData.address = formData.sellerClientAddress || formData.address || '';
      }
      // Apply consumer markup if seller is selling to consumer
      let finalItems = orderItems;
      let markupPercent = 0;
      if (isSellerMode && formData.isConsumerSale && formData.consumerMarkupPercent > 0) {
        markupPercent = formData.consumerMarkupPercent;
        finalItems = orderItems.map(it => (it.isGift ? it : {
          ...it,
          unitPrice: Math.round(it.unitPrice * (1 + markupPercent / 100)),
          totalPrice: Math.round(it.unitPrice * (1 + markupPercent / 100)) * it.quantity,
          originalUnitPrice: it.unitPrice
        }));
      }
      const finalSubtotal = finalItems.reduce((s, it) => s + it.totalPrice, 0);
      const sellerMarkupEarnings = isSellerMode && markupPercent > 0 ? finalSubtotal - getCartTotal() : 0;
      const orderData = {
        ...submitFormData, items: finalItems, shippingMethodName,
        shippingCost: formData.shippingMethod === 'fast' ? shippingSettings.fastShippingCost : 0,
        subtotal: finalSubtotal, total: finalSubtotal + (formData.shippingMethod === 'fast' ? shippingSettings.fastShippingCost : 0),
        orderDate: new Date().toISOString(),
        orderNumber: `BB-${Date.now().toString(36).toUpperCase()}`,
        ...(isSellerMode ? {
          seller_id: user.user_id, seller_name: user.name || user.email,
          seller_client_name: submitFormData.customerName,
          seller_client_business: submitFormData.businessName,
          seller_client_phone: submitFormData.phone,
          seller_client_city: submitFormData.city,
          seller_client_address: submitFormData.address,
          isNewClient: formData.isNewClient || false,
          consumerMarkupPercent: markupPercent,
          sellerMarkupEarnings: sellerMarkupEarnings
        } : {})
      };
      const orderRes = await axios.post(`${API}/orders`, orderData, { withCredentials: true });
      // Marcar cupón como usado si se aplicó
      if (coupon.applied) {
        try {
          await axios.post(`${API}/coupons/${coupon.applied.code}/use`, {});
        } catch (e) { /* ignore */ }
      }
      // Save seller client for future use
      if (isSellerMode && formData.sellerClientName) {
        try {
          await axios.post(`${API}/seller/clients`, {
            name: formData.sellerClientName || formData.customerName,
            businessName: formData.sellerClientBusiness || formData.businessName,
            phone: formData.sellerClientPhone || formData.phone,
            city: formData.sellerClientCity || formData.city,
            address: formData.sellerClientAddress || formData.address
          }, { withCredentials: true });
        } catch (e) { /* ignore save error */ }
      }
      const orderHistory = JSON.parse(localStorage.getItem('bebida-bacana-orders') || '[]');
      orderHistory.push({ 
        date: new Date().toISOString(), orderId: orderData.orderNumber,
        items: cart.map(item => item.id),
        cartItems: cart.map(item => ({
          id: item.id, name: item.name, quantity: item.quantity, unitPrice: getItemPrice(item),
          subtotal: getItemPrice(item) * item.quantity
        })),
        subtotal: getCartTotal(),
        shippingCost: formData.shippingMethod === 'fast' ? shippingSettings.fastShippingCost : 0,
        shippingMethod: shippingMethodName, total: getFinalTotal(), address: formData.address, city: formData.city
      });
      localStorage.setItem('bebida-bacana-orders', JSON.stringify(orderHistory));
      const isSeller = isSellerMode;
      let message = isSeller ? '*PEDIDO DE VENDEDOR - Bebida Bacana*\n\n' : '*Nuevo Pedido - Bebida Bacana*\n\n';
      message += `*Pedido:* ${orderData.orderNumber}\n`;
      if (isSeller) {
        message += `*Vendedor:* ${user.name || user.email}\n`;
        message += `*Cliente del vendedor:* ${formData.sellerClientName || formData.customerName}\n`;
        if (formData.sellerClientBusiness) message += `*Negocio:* ${formData.sellerClientBusiness}\n`;
        if (formData.sellerClientPhone) message += `*Tel. cliente:* ${formData.sellerClientPhone}\n`;
        if (formData.sellerClientCity) message += `*Ciudad cliente:* ${formData.sellerClientCity}\n`;
        if (formData.sellerClientAddress) message += `*Dir. cliente:* ${formData.sellerClientAddress}\n`;
      } else {
        message += `*Cliente:* ${formData.customerName}\n`;
        if (formData.businessName) message += `*Negocio:* ${formData.businessName}\n`;
        if (user?.cuit) message += `*CUIT:* ${user.cuit}\n`;
        if (user?.condicionIva) message += `*Cond. IVA:* ${user.condicionIva}\n`;
      }
      message += `*Dirección:* ${formData.address}${formData.addressNumber ? ' ' + formData.addressNumber : ''}\n*Ciudad:* ${formData.city}${user?.province ? ', '+user.province : ''}\n*Teléfono:* ${formData.phone}\n`;
      if (user?.betweenStreets) message += `*Entre calles:* ${user.betweenStreets}\n`;
      if (user?.googleMapsLink) message += `*Maps:* ${user.googleMapsLink}\n`;
      // Forma de pago dinámica con ajuste
      const pmSelected = _allPaymentMethods.find(p => p.key === formData.paymentMethod);
      const pmLabel = pmSelected?.label || formData.paymentMethod;
      const pmAdj = Number(pmSelected?.adjustPercent || 0);
      if (formData.paymentMethod === 'transferencia') {
        message += '\n⚠️💰 *PAGO POR TRANSFERENCIA* 💰⚠️\n';
        if (user?.cuit) message += `*CUIT: ${user.cuit}*\n`;
      } else {
        message += `*Forma de pago:* ${pmLabel}\n`;
      }
      // NOTA: NO mostramos línea separada "Descuento por X" porque cada
      // producto ya viene con el ajuste aplicado en su precio unitario
      // (como una factura). El cliente ve directamente el precio final.
      if (pmSelected?.note) message += `_${pmSelected.note}_\n`;
      message += '\n';
      message += _pmAdj !== 0
        ? '*Productos:*\n_(precios con la forma de pago aplicada)_\n\n'
        : '*Productos:*\n\n';
      
      // Group combo items together, keep regular items separate
      const comboGroups = {};
      const regularItems = [];
      const giftItems = [];
      cart.forEach(item => {
        if (item.isGift) {
          giftItems.push(item);
        } else if (item.isComboItem && item.comboId) {
          if (!comboGroups[item.comboId]) comboGroups[item.comboId] = { name: item.comboName, items: [] };
          comboGroups[item.comboId].items.push(item);
        } else {
          regularItems.push(item);
        }
      });
      
      // Render combo groups first
      Object.values(comboGroups).forEach(combo => {
        message += `*COMBO ${combo.name.toUpperCase()}*\n`;
        let comboTotal = 0;
        combo.items.forEach(item => {
          const unitPrice = applyPm(item, getItemPrice(item));
          const lineTotal = unitPrice * item.quantity;
          comboTotal += lineTotal;
          message += `  - ${item.quantity}x ${item.name.replace(` (${combo.name})`, '')} $${unitPrice.toLocaleString('es-AR')}\n`;
        });
        message += `  *Precio combo: $${comboTotal.toLocaleString('es-AR')}*\n\n`;
      });
      
      // Render regular items — 🏪 agrupados por sucursal si el user es B2B
      // y hay al menos una sucursal asignada en el carrito. Si no, se emite
      // el listado plano tradicional.
      //
      // ⚖️ Precio por línea: usamos el precio de LISTA (item.priceNumeric),
      // NO el precio con descuentos ya aplicados. Los descuentos se
      // muestran una única vez, agregados, en el bloque de resumen (ver
      // "Subtotal base" + "Descuento por objetivo"). Esto evita que el
      // cliente sume las líneas y le dé un total distinto al TOTAL NETO
      // (efecto "descuento aplicado dos veces").
      const _hasBranchedItems = regularItems.some(it => (it.branchName || '').trim());
      const _renderRegularLine = (item) => {
        const listUnit = Math.round(Number(item.priceNumeric || 0));
        const lineTotal = listUnit * item.quantity;
        const mdp = Number(item.manualDiscountPct || 0);
        let stockNote = '';
        if (item.stock === 0) stockNote = ' ⚠️ CONFIRMAR DISPONIBILIDAD';
        else if (item.stock > 0 && item.quantity > item.stock) stockNote = ` ⚠️ (Stock: ${item.stock} - Confirmar disponibilidad)`;
        let line = `• ${item.quantity} ${item.name} $${listUnit.toLocaleString('es-AR')} total $${Math.round(lineTotal).toLocaleString('es-AR')}${stockNote}`;
        if (mdp > 0) line += ` (desc. vendedor -${mdp}%)`;
        return { line, lineTotal };
      };
      if (_hasBranchedItems) {
        // Agrupar preservando el orden de aparición. Sin asignar va último.
        const _branchGroups = new Map(); // key -> { label, items }
        regularItems.forEach(item => {
          const key = (item.branchName || '').trim().toLowerCase() || '__none__';
          const label = (item.branchName || '').trim() || 'Sin sucursal asignada';
          if (!_branchGroups.has(key)) _branchGroups.set(key, { label, items: [] });
          _branchGroups.get(key).items.push(item);
        });
        // "Sin asignar" al final (si existe).
        const _entries = Array.from(_branchGroups.entries()).sort((a, b) => {
          if (a[0] === '__none__') return 1;
          if (b[0] === '__none__') return -1;
          return 0;
        });
        _entries.forEach(([, group]) => {
          message += `🏪 *SUCURSAL: ${group.label.toUpperCase()}*\n`;
          let branchTotal = 0;
          group.items.forEach(item => {
            const { line, lineTotal } = _renderRegularLine(item);
            branchTotal += lineTotal;
            message += `${line}\n`;
          });
          message += `  _Subtotal ${group.label}: $${Math.round(branchTotal).toLocaleString('es-AR')}_\n\n`;
        });
      } else {
        regularItems.forEach(item => {
          const { line } = _renderRegularLine(item);
          message += `${line}\n\n`;
        });
      }
      // 🎁 Render regalos (Sistema de Premios por Volumen)
      let giftSavingsTotal = 0;
      if (giftItems.length > 0) {
        giftItems.forEach(gi => {
          const orig = Number(gi.giftOriginalPrice || 0) * Number(gi.quantity || 0);
          giftSavingsTotal += orig;
          message += `🎁 *REGALO:* ${gi.name} x${gi.quantity} - $0`;
          if (orig > 0) message += ` _(valor $${Math.round(orig).toLocaleString('es-AR')})_`;
          message += '\n\n';
        });
      }
      const shippingCost = getShippingCost();
      const permanentDiscPct = Number(user?.permanentDiscountPct || 0);
      // 🎟️ Cupón ÚNICO: en el mensaje sólo mostramos la línea del cupón (los
      // demás descuentos quedan suprimidos, igual que en el total cobrado).
      const _single = couponIsSingle();
      // Descuento por objetivo de carrito y % que efectivamente gana el
      // winner-takes-all para clientes con permanentDiscount.
      const rawTotalNow = typeof getRawCartTotal === 'function' ? getRawCartTotal() : 0;
      const cartDiscountPct = (!_blocksVolumeDiscounts && getBestCartDiscountPct) ? Number(getBestCartDiscountPct(rawTotalNow) || 0) : 0;
      // Total base bruto real (precio de lista × cantidad, sin descuentos ni recargos).
      // Los regalos NO cuentan al bruto: se listan como línea aparte con $0.
      const productsBaseGross = cart.reduce((sum, it) => {
        if (it.isGift) return sum;
        if (it.isComboItem) return sum + ((it.priceNumeric || 0) * (it.quantity || 0));
        return sum + Number(it.priceNumeric || 0) * (it.quantity || 0);
      }, 0);
      // Subtotal neto (suma real de las líneas — el TOTAL cierra con esto).
      const productsSubtotalNet = orderItems.reduce((s, it) => s + (it.isGift ? 0 : it.totalPrice), 0);

      // 🔎 Derivar deltas REALES para que la aritmética del mensaje cierre
      // con el TOTAL NETO al peso. Cada delta se calcula del delta real
      // por unidad × qty (no de recomputar un %), evitando el efecto de
      // "descuento aplicado dos veces" que aparece al usar precio de lista
      // en las líneas y luego un porcentaje en el resumen.
      //
      // Cadena de precios: listPrice → winner-takes-all (getPriceForQuantity)
      //  → descuento manual (getItemPrice) → ajuste por forma de pago (applyPm).
      const _pmAdjustPct = Number(_pmSel?.adjustPercent || 0);
      let winnerDiscountDelta = 0;   // ahorro por objetivo/permanente/qty-tier
      let manualDiscountDelta = 0;   // ahorro por descuento manual del vendedor
      let pmAdjustDelta = 0;         // + si el pm reduce, − si el pm recarga
      cart.forEach(it => {
        if (it.isGift) return;
        const qty = Number(it.quantity || 0);
        const unitList = Number(it.priceNumeric || 0);
        const unitWTA = it.isComboItem ? unitList : Number(getPriceForQuantity(it, qty) || 0);
        const unitManual = it.isComboItem ? unitList : Number(getItemPrice(it) || 0);
        const unitFinal = it.isGift ? 0 : Number(applyPm(it, getItemPrice(it)) || 0);
        winnerDiscountDelta += Math.max(0, unitList - unitWTA) * qty;
        manualDiscountDelta += Math.max(0, unitWTA - unitManual) * qty;
        pmAdjustDelta += (unitManual - unitFinal) * qty;
      });
      winnerDiscountDelta = Math.round(winnerDiscountDelta);
      manualDiscountDelta = Math.round(manualDiscountDelta);

      // Determinar etiqueta del descuento winner-takes-all a mostrar.
      const usePermanentLabel = permanentDiscPct > 0 && permanentDiscPct >= cartDiscountPct;
      const useCartTierLabel = cartDiscountPct > 0 && cartDiscountPct > permanentDiscPct;
      // 📦 Bloque de resumen (descuentos + envío + TOTAL NETO) construido una
      // sola vez y reusado tanto por el mensaje completo como por los
      // fallbacks compactos, para que SIEMPRE lleguen los descuentos
      // detallados y el TOTAL NETO (antes el compacto mostraba sólo "TOTAL").
      let summaryBlock = '';
      summaryBlock += `*Subtotal base:* $${Math.round(productsBaseGross).toLocaleString('es-AR')}\n`;
      if (!_single && winnerDiscountDelta > 0) {
        if (useCartTierLabel) {
          summaryBlock += `*🎯 Descuento por objetivo de carrito (${cartDiscountPct}%):* -$${winnerDiscountDelta.toLocaleString('es-AR')}\n`;
        } else if (usePermanentLabel) {
          summaryBlock += `*✨ Descuento permanente cliente (${permanentDiscPct}%):* -$${winnerDiscountDelta.toLocaleString('es-AR')}\n`;
        } else {
          summaryBlock += `*🎯 Descuento aplicado:* -$${winnerDiscountDelta.toLocaleString('es-AR')}\n`;
        }
      }
      if (!_single && manualDiscountDelta > 0) {
        summaryBlock += `*Descuento manual vendedor:* -$${manualDiscountDelta.toLocaleString('es-AR')}\n`;
      }
      // Bonus por perfil completo (aplicado en getFinalTotal, hay que
      // explicitarlo aquí para que la aritmética cierre con el TOTAL NETO).
      const profileBonusAmount = _single ? 0 : Math.round((getProfileBonusAmount?.(user)) || 0);
      if (profileBonusAmount > 0) {
        const pctLabel = profileBonus?.discountPercent ? ` (${profileBonus.discountPercent}%)` : '';
        summaryBlock += `*🎁 Bonus perfil completo${pctLabel}:* -$${profileBonusAmount.toLocaleString('es-AR')}\n`;
      }
      // Ajuste por forma de pago (calculado como delta real).
      if (!_single && Math.abs(pmAdjustDelta) >= 1 && _pmSel) {
        const label = _pmSel?.label || _pmSel?.name || _pmSel?.key || 'Forma de pago';
        const isDiscount = pmAdjustDelta > 0;
        const sign = isDiscount ? '-' : '+';
        summaryBlock += `*💳 ${label} (${_pmAdjustPct > 0 ? '+' : ''}${_pmAdjustPct}%):* ${sign}$${Math.round(Math.abs(pmAdjustDelta)).toLocaleString('es-AR')}\n`;
      }
      // Cupón (si hay).
      const couponAmt = Math.round(getCouponDiscount?.() || 0);
      if (couponAmt > 0) {
        summaryBlock += `*🎟️ Cupón:* -$${couponAmt.toLocaleString('es-AR')}\n`;
      }
      summaryBlock += '\n';
      // 💰 Ahorro total (savings reales + valor de regalos) + porcentaje.
      const discountSavings = Math.max(0, productsBaseGross - Math.max(0, productsSubtotalNet));
      const totalSavings = Math.round(discountSavings + giftSavingsTotal);
      if (totalSavings > 0) {
        const savingsPct = productsBaseGross > 0
          ? Math.round((totalSavings / productsBaseGross) * 100)
          : 0;
        summaryBlock += `*💰 Ahorro total:* $${totalSavings.toLocaleString('es-AR')}`;
        if (giftSavingsTotal > 0) summaryBlock += ` _(incluye $${Math.round(giftSavingsTotal).toLocaleString('es-AR')} en regalos)_`;
        summaryBlock += '\n';
        if (savingsPct > 0) {
          summaryBlock += `*🎉 Ahorraste un ${savingsPct}% en esta compra*\n`;
        }
      }
      summaryBlock += '━━━━━━━━━━━━━━━━━━━━\n';
      summaryBlock += `*MÉTODO DE ENVÍO:*\n*${shippingMethodName}*`;
      if (shippingCost > 0) summaryBlock += ` - ${formatPrice(shippingCost)}`;
      summaryBlock += '\n━━━━━━━━━━━━━━━━━━━━\n\n';
      summaryBlock += `*TOTAL NETO: ${formatPrice(getFinalTotal())}*`;
      message += summaryBlock;
      const encodedMessage = encodeURIComponent(message);
      const MAX_URL_LENGTH = 4000;
      const baseWhatsappURL = `https://wa.me/${shippingSettings.whatsappNumber}`;
      const fullWhatsappURL = `${baseWhatsappURL}?text=${encodedMessage}`;
      // If URL is too long, try shorter message. Last resort: copy to clipboard
      let whatsappURL = fullWhatsappURL;
      let urlTooLong = false;
      // 🏪 Helper reusado por ambos niveles de fallback compacto: agrupa por
      // sucursal igual que el mensaje completo. Antes estos fallbacks
      // listaban todo mezclado (sin separar sucursal) cuando el pedido
      // superaba el límite de longitud de la URL de WhatsApp — bug
      // reportado: pedidos grandes con varias sucursales llegaban mezclados.
      const _groupCartByBranch = () => {
        if (!cart.some(it => (it.branchName || '').trim())) return null;
        const groups = new Map();
        cart.forEach(item => {
          const key = (item.branchName || '').trim().toLowerCase() || '__none__';
          const label = (item.branchName || '').trim() || 'Sin sucursal asignada';
          if (!groups.has(key)) groups.set(key, { label, items: [] });
          groups.get(key).items.push(item);
        });
        return Array.from(groups.entries())
          .sort((a, b) => (a[0] === '__none__' ? 1 : b[0] === '__none__' ? -1 : 0))
          .map(([, g]) => g);
      };
      if (fullWhatsappURL.length > MAX_URL_LENGTH) {
        // Build compact message
        let shortMsg = `*PEDIDO #${orderData.orderNumber}*\n`;
        if (isSeller) shortMsg += `*Vendedor:* ${user.name || user.email}\n*Cliente:* ${formData.sellerClientName || formData.customerName}\n`;
        else shortMsg += `*Cliente:* ${formData.customerName}\n`;
        shortMsg += `*Dir:* ${formData.address}, ${formData.city}\n*Tel:* ${formData.phone}\n\n`;
        const _branchGroups1 = _groupCartByBranch();
        const _renderShortLine = (item) => {
          const _listUnit = Math.round(Number(item.priceNumeric || 0));
          return `${item.quantity}x ${item.name} $${_listUnit.toLocaleString('es-AR')} total $${Math.round(_listUnit * item.quantity).toLocaleString('es-AR')}\n`;
        };
        if (_branchGroups1) {
          _branchGroups1.forEach(group => {
            shortMsg += `🏪 *SUCURSAL: ${group.label.toUpperCase()}*\n`;
            let _bt = 0;
            group.items.forEach(item => {
              if (!item.isGift) _bt += Math.round(Number(item.priceNumeric || 0)) * item.quantity;
              shortMsg += _renderShortLine(item);
            });
            shortMsg += `  _Subtotal ${group.label}: $${Math.round(_bt).toLocaleString('es-AR')}_\n\n`;
          });
        } else {
          cart.forEach(item => { shortMsg += _renderShortLine(item); });
        }
        shortMsg += `\n${summaryBlock}`;
        let shortURL = `${baseWhatsappURL}?text=${encodeURIComponent(shortMsg)}`;
        // Si con precio por línea sigue siendo muy largo, se cae a la versión
        // sin precio por línea (pero SIGUE mostrando todos los productos,
        // y SIGUE separando por sucursal).
        if (shortURL.length > MAX_URL_LENGTH) {
          shortMsg = `*PEDIDO #${orderData.orderNumber}*\n`;
          if (isSeller) shortMsg += `*Vendedor:* ${user.name || user.email}\n*Cliente:* ${formData.sellerClientName || formData.customerName}\n`;
          else shortMsg += `*Cliente:* ${formData.customerName}\n`;
          shortMsg += `*Dir:* ${formData.address}, ${formData.city}\n*Tel:* ${formData.phone}\n\n`;
          const _branchGroups2 = _groupCartByBranch();
          if (_branchGroups2) {
            _branchGroups2.forEach(group => {
              shortMsg += `🏪 *SUCURSAL: ${group.label.toUpperCase()}*\n`;
              let _bt2 = 0;
              group.items.forEach(item => {
                if (!item.isGift) _bt2 += Math.round(Number(item.priceNumeric || 0)) * item.quantity;
                shortMsg += `${item.quantity}x ${item.name}\n`;
              });
              shortMsg += `  _Subtotal ${group.label}: $${Math.round(_bt2).toLocaleString('es-AR')}_\n\n`;
            });
          } else {
            cart.forEach(item => { shortMsg += `${item.quantity}x ${item.name}\n`; });
          }
          shortMsg += `\n${summaryBlock}`;
          shortURL = `${baseWhatsappURL}?text=${encodeURIComponent(shortMsg)}`;
        }
        if (shortURL.length <= MAX_URL_LENGTH) {
          whatsappURL = shortURL;
        } else {
          urlTooLong = true;
          whatsappURL = baseWhatsappURL;
          if (navigator.clipboard) { try { await navigator.clipboard.writeText(message); } catch (_) {} }
        }
      }
      // Build client WhatsApp URL for seller orders
      let clientWhatsappURL = null;
      let clientRawMessage = null;
      if (isSeller) {
        const clientWa = orderRes.data?.client_whatsapp;
        if (clientWa && clientWa.client_phone) {
          clientRawMessage = clientWa.client_whatsapp_message;
          const encodedClientMsg = encodeURIComponent(clientRawMessage);
          clientWhatsappURL = `https://wa.me/${clientWa.client_phone}?text=${encodedClientMsg}`;
        }
      }
      setConfirmedOrder({
        ...orderData, whatsappURL, messageCopied: urlTooLong, rawMessage: message,
        clientWhatsappURL, clientRawMessage,
        // Sobrescribimos subtotal/total con los valores reales del checkout
        // (getFinalTotal incluye bonus perfil, ajuste por forma de pago,
        // cupón y envío). Antes el panel mostraba el subtotal crudo del
        // orderData, que no cerraba con lo que realmente se cobró.
        subtotal: Math.round(productsSubtotalNet),
        total: Math.round(getFinalTotal()),
        formattedItems: cart.map(item => ({
          ...item,
          unitPrice: getItemPrice(item),
          totalPrice: getItemPrice(item) * item.quantity
        }))
      });
      setSuccess(true);
      clearCart();
      // 📲 Apertura de WhatsApp:
      // - En móviles (iOS y Android) usamos `window.location.href` que dispara
      //   el deep-link a la app de WhatsApp. window.open() es bloqueado por
      //   muchos navegadores móviles cuando se llama luego de un await.
      // - En desktop intentamos window.open en nueva pestaña; si el navegador
      //   lo bloquea, la pantalla de confirmación tiene un botón visible para
      //   abrir manualmente.
      const ua = navigator.userAgent || '';
      const isMobile = /iPhone|iPad|iPod|Android/i.test(ua);
      if (isMobile) {
        // Pequeño delay para que React termine de pintar la pantalla de éxito
        // antes de redirigir.
        setTimeout(() => { window.location.href = whatsappURL; }, 150);
      } else {
        const win = window.open(whatsappURL, '_blank', 'noopener,noreferrer');
        if (!win) {
          // Pop-up bloqueado: fallback a navegar en la misma pestaña.
          // El usuario también tiene el botón verde en la pantalla de éxito.
          setTimeout(() => { window.location.href = whatsappURL; }, 100);
        }
      }
    } catch (error) {
      console.error('Error creating order:', error?.response?.status, error?.response?.data, error);
      // Mostrar el detalle real al usuario (validación 422, 4xx, 5xx, etc.)
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      let detailText = '';
      if (typeof detail === 'string') {
        detailText = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation error array
        detailText = detail.map(d => {
          const path = (d.loc || []).filter(x => x !== 'body').join('.');
          return `${path}: ${d.msg}`;
        }).join(' · ');
      } else if (detail) {
        detailText = JSON.stringify(detail);
      }
      const friendly = !navigator.onLine
        ? 'Sin conexión a internet. Probá de nuevo.'
        : status
          ? `No pudimos crear tu pedido (${status})${detailText ? ': ' + detailText : ''}. Si persiste, escribinos por WhatsApp.`
          : 'Error de red al enviar el pedido. Probá de nuevo.';
      setSubmitError(friendly);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally { setLoading(false); }
  };

  if (!isCartOpen) return null;
  
  // ── Order Confirmed Screen ──
  if (success && confirmedOrder) {
    return (
      <>
        <div className='fixed inset-0 bg-black/40 z-50' />
        <div className='fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-[60] flex flex-col'>          <div className='p-6 text-center bg-gradient-to-b from-emerald-50 to-white'>
            <div className='w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3'>
              <Check size={28} className='text-emerald-600' />
            </div>
            <h2 className='text-xl font-bold text-slate-800' style={{ fontFamily: 'Barlow Condensed' }}>Pedido Confirmado</h2>
            <p className='text-sm text-slate-500 mt-1'>Enviado por WhatsApp</p>
          </div>
          <div className='flex-1 overflow-y-auto px-5 pb-4'>
            <div className='bg-slate-50 rounded-lg p-3 mb-4'>
              <p className='text-xs text-slate-400'>N° de pedido</p>
              <p className='text-lg font-bold text-slate-800' style={{ fontFamily: 'Barlow Condensed' }}>{confirmedOrder.orderNumber}</p>
            </div>
            <p className='text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3'>Resumen</p>
            <div className='space-y-2 mb-4'>
              {confirmedOrder.formattedItems.map((item, idx) => (
                <div key={idx} className='flex justify-between items-center py-2 border-b border-slate-100 last:border-0'>
                  <div>
                    <p className='text-sm font-medium text-slate-700'>{item.name}</p>
                    <p className='text-xs text-slate-400'>x{item.quantity}</p>
                  </div>
                  <p className='text-sm font-semibold text-slate-800'>{formatPrice(item.totalPrice)}</p>
                </div>
              ))}
            </div>
            <div className='bg-slate-50 rounded-lg p-4'>
              <div className='flex justify-between text-sm mb-1'>
                <span className='text-slate-500'>Subtotal</span>
                <span className='text-slate-700'>{formatPrice(confirmedOrder.subtotal)}</span>
              </div>
              <div className='flex justify-between text-sm mb-2'>
                <span className='text-slate-500'>Envío</span>
                <span className={confirmedOrder.shippingCost === 0 ? 'text-emerald-600 font-medium' : 'text-slate-700'}>
                  {confirmedOrder.shippingCost === 0 ? 'GRATIS' : formatPrice(confirmedOrder.shippingCost)}
                </span>
              </div>
              <div className='flex justify-between pt-2 border-t border-slate-200'>
                <span className='font-bold text-slate-800'>Total</span>
                <span className='text-lg font-bold' style={{ color: theme.primary, fontFamily: 'Barlow Condensed' }}>{formatPrice(confirmedOrder.total)}</span>
              </div>
            </div>
          </div>
          <div className='p-4 border-t border-slate-100 space-y-2'>
            <div className='p-3 bg-emerald-50 rounded-xl border border-emerald-200 text-center mb-2'>
              <p className='text-sm font-bold text-emerald-800 mb-1'>📲 ¿Se abrió WhatsApp?</p>
              <p className='text-xs text-emerald-700'>Si no se abrió automáticamente, tocá el botón verde acá abajo.</p>
            </div>
            <a href={confirmedOrder.whatsappURL} target='_blank' rel='noopener noreferrer'
              className='w-full py-4 rounded-xl font-bold text-white text-base transition-opacity hover:opacity-90 flex items-center justify-center gap-2 shadow-lg'
              style={{ backgroundColor: '#25D366' }}
              data-testid='resend-whatsapp-btn'>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.413c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.523 5.255l-.999 3.648 3.965-.602zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/></svg>
              Enviar pedido por WhatsApp
            </a>
            {confirmedOrder.messageCopied && (
              <div className='p-3 bg-amber-50 rounded-xl border border-amber-200 text-center'>
                <p className='text-sm font-medium text-amber-800 mb-1'>El pedido fue copiado al portapapeles</p>
                <p className='text-xs text-amber-600'>Pegalo en el chat de WhatsApp</p>
              </div>
            )}
            {confirmedOrder.rawMessage && (
              <button onClick={() => {
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(confirmedOrder.rawMessage);
                  alert('Mensaje copiado al portapapeles');
                }
              }}
                className='w-full py-2.5 rounded-xl font-medium text-sm border-2 transition-colors hover:bg-slate-50'
                style={{ borderColor: theme.primary, color: theme.primary }}
                data-testid='copy-message-btn'>
                Copiar mensaje del pedido
              </button>
            )}
            {confirmedOrder.clientWhatsappURL && (
              <a href={confirmedOrder.clientWhatsappURL} target='_blank' rel='noopener noreferrer'
                className='w-full py-3 rounded-xl font-semibold text-white transition-opacity hover:opacity-90 flex items-center justify-center gap-2'
                style={{ backgroundColor: '#128C7E' }}
                data-testid='send-client-whatsapp-btn'>
                Enviar al Cliente por WhatsApp
              </a>
            )}
            <button onClick={() => { setSuccess(false); setConfirmedOrder(null); setShowCheckout(false); setIsCartOpen(false); }}
              className='w-full py-3 rounded-xl font-semibold text-white transition-opacity hover:opacity-90'
              style={{ backgroundColor: theme.primary }} data-testid='close-confirmation-btn'>
              Cerrar
            </button>
          </div>
        </div>
      </>
    );
  }


  // 🏪 Renderer de una línea del carrito (extraído del map para poder
  // agrupar por sucursal). Los items `isGift` mantienen su render especial.
  // La línea NO gift trae un pill editable de sucursal (edición inline).
  const renderCartItem = (item) => {
    const unitPrice = getItemPrice(item);
    const packSize = item.packSize || 1;
    const minQuantity = Math.max(1, Number(item.minQuantity) || 1);
    const isPack = packSize > 1;
    const displayTotalPrice = unitPrice * item.quantity;
    const displayUnitPrice = unitPrice;

    // 🎁 Regalo por objetivo de carrito: render especial (no editable, precio $0).
    if (item.isGift) {
      const giftBase = Number(item.giftOriginalPrice || 0);
      return (
        <div className='py-3 relative' data-testid={`cart-item-gift-${item.id}`}>
          <div className='flex gap-3 bg-gradient-to-r from-amber-50 via-orange-50 to-pink-50 border border-orange-200 rounded-xl p-3'>
            <div className='relative flex-shrink-0'>
              <img
                src={encodeURI(item.imageUrl || '')}
                alt={item.name}
                className='w-16 h-16 object-contain bg-white rounded-lg border-2 border-orange-300'
                loading="lazy"
              />
              <span className='absolute -top-2 -right-2 text-xl' aria-hidden>🎁</span>
            </div>
            <div className='flex-1 min-w-0'>
              <div className='flex items-center gap-2 mb-0.5'>
                <span className='text-[10px] font-black uppercase tracking-widest bg-orange-500 text-white px-1.5 py-0.5 rounded'>Regalo</span>
                <span className='text-[10px] font-bold text-orange-600'>x{item.quantity}</span>
              </div>
              <h3 className='text-sm font-semibold text-slate-800 line-clamp-2 leading-tight' translate="no" data-testid={`cart-gift-name-${item.id}`}>{item.name}</h3>
              <div className='flex items-center gap-2 mt-1'>
                <span className='text-sm font-bold text-emerald-700'>$0</span>
                {giftBase > 0 && (
                  <span className='text-xs text-slate-400 line-through' data-testid={`cart-gift-original-${item.id}`}>
                    {formatPrice(giftBase * item.quantity)}
                  </span>
                )}
              </div>
              <p className='text-[10px] text-orange-700 mt-1 font-medium'>
                {item.isPromoGift
                  ? `🎉 ¡Bonificación! ${item.promoName || 'Te llevás este producto de regalo'}`
                  : '🎉 Te llevás este producto de regalo por alcanzar el objetivo del carrito'}
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Next discount tier — consumidor final NO ve estos hints.
    const _effCart = getEffectiveCustomerType?.();
    const _isConsumerFinal = _effCart === 'consumidor_final';
    let nextTier = null;
    if (!_isConsumerFinal && item.quantityPrices && item.quantityPrices.length > 0) {
      let currentTierIndex = -1;
      for (let i = item.quantityPrices.length - 1; i >= 0; i--) {
        if (item.quantity >= item.quantityPrices[i].minQty) { currentTierIndex = i; break; }
      }
      const nextTierIndex = currentTierIndex + 1;
      if (nextTierIndex < item.quantityPrices.length) {
        const tier = item.quantityPrices[nextTierIndex];
        nextTier = {
          minQty: tier.minQty, minUnits: tier.minQty, price: tier.priceNumeric,
          unitsNeeded: tier.minQty - item.quantity,
          savings: (unitPrice - tier.priceNumeric) * tier.minQty
        };
      }
    }

    return (
      <div className='py-3' data-testid={`cart-item-${item.id}`}>
        <div className='flex gap-3'>
          <img src={encodeURI(item.imageUrl || '') + (item.imageUrl ? '?w=150' : '')} alt={item.name} className='w-14 h-14 object-contain bg-slate-50 rounded-lg flex-shrink-0' loading="lazy" />
          <div className='flex-1 min-w-0'>
            <div className='flex items-start justify-between gap-2'>
              <div className='min-w-0'>
                <h3 className='text-sm font-medium text-slate-800 line-clamp-2 leading-tight' data-testid={`cart-item-name-${item.id}`} translate="no">{item.name}</h3>
                {isPack && <p className='text-xs text-slate-400 mt-0.5'>Pack x{packSize}</p>}
              </div>
              <button onClick={() => removeFromCart(item.id)} className='text-slate-300 hover:text-red-400 p-0.5 transition-colors flex-shrink-0' data-testid={`cart-remove-${item.id}`}>
                <X size={16} />
              </button>
            </div>
            <div className='flex items-center justify-between mt-2'>
              <div>
                <p className='text-sm font-bold text-slate-800' data-testid={`cart-item-subtotal-${item.id}`}>{formatPrice(displayTotalPrice)}</p>
              </div>
              {item.isComboItem ? (
                <span className='text-xs font-bold text-slate-500 bg-slate-100 px-3 py-1.5 rounded-lg'>x{item.quantity}</span>
              ) : (
                <div className='flex items-center gap-1.5'>
                  <button onClick={() => { const next = item.quantity - minQuantity; if (next < minQuantity) updateQuantity(item.id, 0); else updateQuantity(item.id, next); }} className='w-9 h-9 bg-slate-100 hover:bg-slate-200 rounded-lg flex items-center justify-center text-slate-600 text-base font-bold transition-colors' data-testid={`cart-decrease-${item.id}`}>−</button>
                  <input type='text' inputMode='numeric' value={item.quantity}
                    onChange={(e) => {
                      const val = parseInt(e.target.value.replace(/\D/g, '')) || 0;
                      // 🚦 Snap a múltiplos de minQuantity. 1..(min-1) → 0.
                      let target = 0;
                      if (val > 0) {
                        if (val < minQuantity) target = 0;
                        else target = Math.round(val / minQuantity) * minQuantity;
                      }
                      updateQuantity(item.id, target);
                    }}
                    onFocus={(e) => e.target.select()}
                    className='w-14 px-1 py-1.5 border border-slate-200 rounded-lg text-center font-bold text-sm focus:border-slate-400 focus:outline-none'
                    data-testid={`cart-quantity-${item.id}`}
                  />
                  <button onClick={() => updateQuantity(item.id, item.quantity + minQuantity)} className='w-9 h-9 text-white rounded-lg flex items-center justify-center text-base font-bold transition-opacity hover:opacity-90' style={{ backgroundColor: theme.primary }} data-testid={`cart-increase-${item.id}`}>+</button>
                </div>
              )}
            </div>
            {/* 🏪 Selector inline de sucursal REMOVIDO — la asignación se
                hace 100% desde el selector global (BranchSelector) al momento
                de agregar cada producto. El agrupamiento por sucursal sigue
                visible en el bloque de resumen del carrito. */}
            {/* Descuento manual del vendedor por línea (solo si isSellerMode + maxManualDiscountPct > 0). */}
            {isSellerMode && !item.isComboItem && (storeSettings?.maxManualDiscountPct || 0) > 0 && (() => {
              const maxPct = Number(storeSettings.maxManualDiscountPct || 0);
              const currentPct = Number(item.manualDiscountPct || 0);
              return (
                <div className='flex items-center gap-2 mt-1.5 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1'>
                  <span className='text-[10px] font-black text-emerald-700 uppercase tracking-wider'>Desc. vendedor</span>
                  <input
                    type='number'
                    min='0'
                    max={maxPct}
                    step='0.5'
                    value={currentPct || ''}
                    placeholder='0'
                    onChange={(e) => {
                      const v = parseFloat(e.target.value) || 0;
                      if (v > maxPct) {
                        toast.error(`Descuento máximo por línea: ${maxPct}%`, {
                          description: 'Se ajustó automáticamente al tope permitido.',
                          duration: 3500,
                        });
                        setItemManualDiscount(item.id, maxPct);
                      } else {
                        setItemManualDiscount(item.id, v);
                      }
                    }}
                    className='w-14 px-1 py-0.5 border border-emerald-300 rounded text-center text-xs font-bold focus:border-emerald-500 focus:outline-none'
                    data-testid={`cart-manual-discount-${item.id}`}
                  />
                  <span className='text-[10px] font-bold text-emerald-700'>% (máx {maxPct}%)</span>
                </div>
              );
            })()}

            {item.stock >= 0 && item.quantity > item.stock && (
              <p className='text-[10px] text-amber-600 font-medium mt-1 bg-amber-50 px-2 py-1 rounded'>
                Sin stock - Te confirmamos disponibilidad
              </p>
            )}
            <div className='flex items-center gap-2 mt-0.5'>
              <p className='text-xs text-slate-400'>{formatPrice(displayUnitPrice)} c/u</p>
              {item.isComboItem && (
                <span className='text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded font-medium'>
                  COMBO
                </span>
              )}
            </div>
            {item.stock === 0 && (
              <p className='text-[10px] text-amber-600 font-semibold mt-0.5'>Ingresa pronto - Se confirmará disponibilidad</p>
            )}
          </div>
        </div>
        {nextTier && !item.isComboItem && (
          <button onClick={() => { updateQuantity(item.id, isPack ? nextTier.minUnits : nextTier.minQty); }}
            className='mt-2 w-full text-xs text-left p-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors'>
            Agregá {nextTier.unitsNeeded} más y ahorrás <strong>${nextTier.savings.toLocaleString('es-AR')}</strong>
          </button>
        )}
      </div>
    );
  };


  // ── Main Cart ──
  return (
    <>
      <div className='fixed inset-0 bg-black/40 z-50' onClick={() => { setIsCartOpen(false); setShowCheckout(false); }} data-testid='cart-overlay' />
      <div className='fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-[60] flex flex-col' data-testid='cart-sidebar'>
        {/* Header */}
        <div className='flex items-center justify-between px-5 py-4 border-b border-slate-100'>
          <h2 className='text-lg font-bold text-slate-800' style={{ fontFamily: 'Barlow Condensed' }} data-testid='cart-title'>
            {showCheckout ? 'Finalizar Pedido' : `Carrito (${cart.length})`}
          </h2>
          <button onClick={() => { setIsCartOpen(false); setShowCheckout(false); }} className='p-2 hover:bg-slate-100 rounded-lg text-slate-400 transition-colors' data-testid='cart-close-button'>
            <X size={20} />
          </button>
        </div>

        {/* Aviso de precios actualizados */}
        {priceUpdate && priceUpdate.count > 0 && (
          <div className='px-5 py-3 bg-amber-50 border-b border-amber-200' data-testid='price-update-notice'>
            <div className='flex items-start gap-2'>
              <AlertCircle size={16} className='text-amber-600 flex-shrink-0 mt-0.5' />
              <div className='flex-1'>
                <p className='text-xs font-bold text-amber-800'>
                  Actualizamos {priceUpdate.count === 1 ? '1 precio' : `${priceUpdate.count} precios`} de tu carrito
                </p>
                <ul className='mt-1 text-[11px] text-amber-700 space-y-0.5 max-h-24 overflow-y-auto'>
                  {priceUpdate.items.slice(0, 5).map((c, i) => (
                    <li key={i}>
                      <b>{c.name}</b>: {c.newPrice === null
                        ? <span className='text-rose-700'>ya no disponible</span>
                        : <>de {formatPrice(c.oldPrice)} a <b>{formatPrice(c.newPrice)}</b></>
                      }
                    </li>
                  ))}
                  {priceUpdate.items.length > 5 && (
                    <li className='italic'>… y {priceUpdate.items.length - 5} más</li>
                  )}
                </ul>
              </div>
              <button onClick={dismissPriceUpdate} className='text-amber-600 hover:text-amber-800 p-0.5' aria-label='Cerrar'>
                <X size={14} />
              </button>
            </div>
          </div>
        )}

        {cart.length === 0 ? (
          <div className='flex-1 flex flex-col items-center justify-center text-slate-400 p-8' data-testid='cart-empty'>
            <ShoppingCart size={48} className='mb-4 text-slate-200' />
            <p className='text-base text-slate-500 mb-1'>Tu carrito está vacío</p>
            <p className='text-sm text-slate-400 mb-6'>Agregá productos desde el catálogo</p>
            <button onClick={() => { setIsCartOpen(false); navigate('/products'); }}
              className='px-6 py-2.5 text-white font-medium rounded-xl text-sm transition-opacity hover:opacity-90'
              style={{ backgroundColor: theme.primary }} data-testid='go-to-catalog-btn'>
              Ir al Catálogo
            </button>
          </div>
        ) : showCheckout ? (
          /* ── Checkout Form ── */
          <>
          <form onSubmit={handleSubmit} className='flex-1 overflow-y-auto px-5 py-4 space-y-4' data-cart-checkout-form='true'>
            {/* Admin: Toggle between personal and seller mode */}
            {user?.role === 'admin' && (
              <div className='flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200'>
                <label className='flex items-center gap-2 cursor-pointer flex-1'>
                  <input type='checkbox' checked={orderAsVendedor} onChange={e => {
                    const checked = e.target.checked;
                    if (checked) {
                      // Save current commerce data before switching to seller mode
                      setSavedCommerceData({
                        customerName: formData.customerName,
                        businessName: formData.businessName,
                        address: formData.address,
                        city: formData.city,
                        phone: formData.phone
                      });
                      // Clear form for seller client input
                      setFormData(prev => ({
                        ...prev,
                        customerName: '', businessName: '', address: '', city: '', phone: '',
                        sellerClientName: '', sellerClientBusiness: '', sellerClientPhone: '', sellerClientCity: '', sellerClientAddress: ''
                      }));
                    } else {
                      // Restore saved commerce data
                      const saved = savedCommerceData || shippingData || {};
                      setFormData(prev => ({
                        ...prev,
                        customerName: saved.customerName || shippingData.customerName || '',
                        businessName: saved.businessName || shippingData.businessName || '',
                        address: saved.address || shippingData.address || '',
                        city: saved.city || shippingData.city || '',
                        phone: saved.phone || shippingData.phone || '',
                        sellerClientName: '', sellerClientBusiness: '', sellerClientPhone: '', sellerClientCity: '', sellerClientAddress: ''
                      }));
                      setClientSearch('');
                    }
                    setOrderAsVendedor(checked);
                  }}
                    className='w-4 h-4 rounded' data-testid='toggle-seller-mode' />
                  <span className='text-sm font-medium text-slate-700'>Hacer pedido como vendedor</span>
                </label>
              </div>
            )}
            {/* Seller Client Section */}
            {isSellerMode && (
              <div className='p-3 bg-amber-50 rounded-xl border border-amber-200 space-y-3'>
                <p className='text-sm font-bold text-amber-800'>Datos del cliente (vendedor)</p>
                <div className='relative'>
                  <input
                    type='text'
                    value={clientSearch}
                    onChange={(e) => { setClientSearch(e.target.value); setShowClientList(true); }}
                    onFocus={() => setShowClientList(true)}
                    placeholder='Buscar cliente o crear nuevo...'
                    className='form-input text-sm'
                    data-testid='seller-client-search'
                  />
                  {showClientList && clientSearch && (
                    <div className='absolute top-full mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg z-20 max-h-48 overflow-y-auto'>
                      {sellerClients.filter(c => 
                        (c.name || '').toLowerCase().includes(clientSearch.toLowerCase()) ||
                        (c.businessName || '').toLowerCase().includes(clientSearch.toLowerCase())
                      ).map((c, i) => {
                        const daysSinceOrder = c.lastOrderDate ? Math.floor((Date.now() - new Date(c.lastOrderDate).getTime()) / 86400000) : null;
                        const statusColor = daysSinceOrder === null ? '#94A3B8' : daysSinceOrder <= 7 ? '#10B981' : daysSinceOrder <= 30 ? '#F59E0B' : '#EF4444';
                        return (
                          <button key={i} type='button' onClick={() => selectSellerClient(c)}
                            className='w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b border-slate-50 flex items-center gap-2'>
                            <div className='w-2 h-2 rounded-full flex-shrink-0' style={{ backgroundColor: statusColor }}></div>
                            <div className='flex-1 min-w-0'>
                              <span className='font-medium block truncate'>{c.businessName || c.name}</span>
                              <span className='text-slate-400 text-xs'>{c.name}{c.city ? ` - ${c.city}` : ''}</span>
                            </div>
                            {c.orderCount > 0 && <span className='text-[10px] text-slate-400'>{c.orderCount} ped.</span>}
                          </button>
                        );
                      })}
                      {sellerClients.filter(c => 
                        (c.name || '').toLowerCase().includes(clientSearch.toLowerCase()) ||
                        (c.businessName || '').toLowerCase().includes(clientSearch.toLowerCase())
                      ).length === 0 && (
                        <p className='px-3 py-2 text-xs text-slate-400'>Sin resultados. Completá los datos abajo.</p>
                      )}
                    </div>
                  )}
                </div>
                <div className='grid grid-cols-2 gap-2'>
                  <input type='text' placeholder='Nombre cliente' value={formData.sellerClientName || ''} 
                    onChange={(e) => setFormData(prev => ({ ...prev, sellerClientName: e.target.value }))}
                    className='form-input text-sm' data-testid='seller-client-name' />
                  <input type='text' placeholder='Negocio' value={formData.sellerClientBusiness || ''} 
                    onChange={(e) => setFormData(prev => ({ ...prev, sellerClientBusiness: e.target.value }))}
                    className='form-input text-sm' data-testid='seller-client-business' />
                  <input type='tel' placeholder='Teléfono' value={formData.sellerClientPhone || ''} 
                    onChange={(e) => setFormData(prev => ({ ...prev, sellerClientPhone: e.target.value }))}
                    className='form-input text-sm' data-testid='seller-client-phone' />
                  <input type='text' placeholder='Ciudad' value={formData.sellerClientCity || ''} 
                    onChange={(e) => setFormData(prev => ({ ...prev, sellerClientCity: e.target.value }))}
                    className='form-input text-sm' data-testid='seller-client-city' />
                </div>
                <input type='text' placeholder='Dirección del cliente' value={formData.sellerClientAddress || ''} 
                  onChange={(e) => setFormData(prev => ({ ...prev, sellerClientAddress: e.target.value }))}
                  className='form-input text-sm' data-testid='seller-client-address' />
                
                {/* New client toggle + Consumer sale toggle */}
                <div className='flex gap-2 flex-wrap'>
                  {sellerProfile?.new_client_bonus_enabled && (
                    <label className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-xs font-medium transition-colors border ${formData.isNewClient ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-slate-200 text-slate-500'}`} data-testid='new-client-toggle'>
                      <input type='checkbox' checked={formData.isNewClient || false}
                        onChange={(e) => setFormData(prev => ({ ...prev, isNewClient: e.target.checked }))}
                        className='w-4 h-4 accent-emerald-600' />
                      Cliente Nuevo
                    </label>
                  )}
                  {sellerProfile?.consumer_sale_enabled && (
                    <label className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-xs font-medium transition-colors border ${formData.isConsumerSale ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-slate-200 text-slate-500'}`} data-testid='consumer-sale-toggle'>
                      <input type='checkbox' checked={formData.isConsumerSale || false}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData(prev => ({
                            ...prev,
                            isConsumerSale: checked,
                            consumerMarkupPercent: checked ? (sellerProfile?.consumer_markup_percent || prev.consumerMarkupPercent || 0) : 0
                          }));
                        }}
                        className='w-4 h-4 accent-blue-600' />
                      Venta a Consumidor Final
                    </label>
                  )}
                </div>
                {formData.isConsumerSale && (
                  <div className='p-2 bg-blue-50 rounded-lg border border-blue-200'>
                    <label className='block text-xs font-medium text-blue-700 mb-1'>Tu recargo sobre precio mayorista (%)</label>
                    <input type='number' value={formData.consumerMarkupPercent || 0}
                      onChange={(e) => setFormData(prev => ({ ...prev, consumerMarkupPercent: parseFloat(e.target.value) || 0 }))}
                      className='w-full px-3 py-1.5 text-sm border border-blue-200 rounded-lg bg-white'
                      min='0' max='200' step='5' placeholder='Ej: 30'
                      data-testid='consumer-markup-input' />
                    <p className='text-[10px] text-blue-500 mt-1'>Este % se suma al precio mayorista. La diferencia es tu ganancia.</p>
                  </div>
                )}
                
                {/* Client purchase history - quick add */}
                {loadingHistory && (
                  <div className='flex items-center gap-2 text-xs text-amber-600'>
                    <div className='animate-spin h-3 w-3 border border-amber-600 border-t-transparent rounded-full'></div>
                    Cargando historial...
                  </div>
                )}
                {clientHistory && clientHistory.frequent_products?.length > 0 && (
                  <div className='mt-2 p-2 bg-white rounded-lg border border-amber-100'>
                    <p className='text-xs font-bold text-amber-800 mb-1.5'>Productos habituales de {formData.sellerClientName || 'este cliente'} ({clientHistory.total_orders} pedidos)</p>
                    <div className='space-y-1 max-h-32 overflow-y-auto'>
                      {clientHistory.frequent_products.slice(0, 8).map((fp, i) => {
                        const inCart = cart.find(c => c.id === fp.id);
                        return (
                          <div key={i} className='flex items-center justify-between gap-1 text-xs py-1 border-b border-slate-50 last:border-0'>
                            <div className='flex-1 min-w-0'>
                              <span className='font-medium text-slate-700 truncate block'>{fp.name}</span>
                              <span className='text-slate-400'>x{fp.lastQty} habitual - {fp.orderCount} veces</span>
                            </div>
                            {inCart ? (
                              <span className='text-green-600 font-bold text-[10px] px-2 py-0.5 bg-green-50 rounded-full'>En carrito</span>
                            ) : (
                              <button type='button' onClick={() => addToCart({ id: fp.id, name: fp.name, imageUrl: fp.imageUrl, price: fp.lastPrice, priceNumeric: fp.lastPrice, packSize: fp.packSize || 1 }, fp.lastQty || fp.packSize || 1)}
                                className='px-2 py-0.5 text-[10px] font-bold text-white rounded-full' style={{ backgroundColor: theme.primary }}
                                data-testid={`quick-add-${fp.id}`}>
                                + {fp.lastQty}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {clientHistory.orders?.[0] && (
                      <button type='button' onClick={() => {
                        const lastOrder = clientHistory.orders[0];
                        const items = lastOrder.items || lastOrder.cartItems || [];
                        items.forEach(item => {
                          if (!cart.find(c => c.id === (item.id || item.productId))) {
                            addToCart({
                              id: item.id || item.productId,
                              name: item.name || item.productName || '',
                              imageUrl: item.imageUrl || item.image_url || '',
                              price: item.price || item.unitPrice || item.priceNumeric || 0,
                              priceNumeric: item.price || item.unitPrice || item.priceNumeric || 0,
                              packSize: item.packSize || 1
                            }, item.quantity || 1);
                          }
                        });
                      }}
                        className='mt-2 w-full py-1.5 text-xs font-bold text-amber-800 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors'
                        data-testid='repeat-last-order-btn'>
                        Repetir último pedido ({clientHistory.orders[0].items?.length || 0} productos)
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            {/* Regular form fields - hidden for sellers who already filled client data */}
            {!isSellerMode && (
              <>
                <FormField label="Nombre Completo" required>
                  <input type='text' name='customerName' value={formData.customerName} onChange={handleChange} required className='form-input' data-testid='checkout-customer-name' />
                </FormField>
                <FormField label={`Nombre del Negocio ${user?.customer_type === 'comercio' ? '' : '(opcional)'}`} required={user?.customer_type === 'comercio'}>
                  <input type='text' name='businessName' value={formData.businessName} onChange={handleChange} required={user?.customer_type === 'comercio'} className='form-input' />
                </FormField>
                <FormField label="Dirección" required>
                  <input type='text' name='address' value={formData.address} onChange={handleChange} required className='form-input' />
                </FormField>
                <FormField label="Ciudad" required>
                  <input type='text' name='city' value={formData.city} onChange={handleChange} required className='form-input' />
                </FormField>
                <FormField label="Teléfono" required>
                  <input type='tel' name='phone' value={formData.phone} onChange={handleChange} required className='form-input' />
                </FormField>
              </>
            )}

            {/* Payment — métodos configurables con descuento/recargo */}
            <FormField label="Forma de Pago">
              <div className='space-y-2'>
                {(() => {
                  const isConsumerFinal = getEffectiveCustomerType?.() === 'consumidor_final';
                  // Si es consumidor final usamos consumerPaymentMethods, sino paymentMethods.
                  const list = isConsumerFinal
                    ? (storeSettings?.consumerPaymentMethods || storeSettings?.paymentMethods || [
                        { key: 'efectivo', label: 'Efectivo', adjustPercent: 0, enabled: true },
                        { key: 'transferencia', label: 'Transferencia', adjustPercent: 0, enabled: true, showCuit: true },
                      ])
                    : (storeSettings?.paymentMethods || [
                        { key: 'efectivo', label: 'Efectivo', adjustPercent: 0, enabled: true },
                        { key: 'transferencia', label: 'Transferencia', adjustPercent: 0, enabled: true, showCuit: true },
                      ]);
                  return list.filter(m => {
                    if (m.enabled === false) return false;
                    if (m.restricted === true) {
                      const allowed = user?.allowedPaymentMethods || [];
                      return allowed.includes(m.key);
                    }
                    return true;
                  }).map(m => {
                    const adj = Number(m.adjustPercent || 0);
                    const lbl = adj === 0 ? m.label
                      : adj > 0 ? `${m.label} (+${adj}%)`
                      : `${m.label} (${adj}%)`;
                    // Monto mínimo por método: si el subtotal no alcanza, se muestra
                    // deshabilitado con un mensaje "te faltan $X para acceder".
                    const minAmt = Number(m.minAmount || 0);
                    const subtotal = (typeof getRawCartTotal === 'function') ? Number(getRawCartTotal() || 0) : 0;
                    const missing = Math.max(0, minAmt - subtotal);
                    const isDisabledByMin = minAmt > 0 && missing > 0;

                    // 💰 Regla clave: si el carrito tiene ítems con precio por
                    // cantidad activo (tier), solo los métodos EFECTIVO
                    // (acceptsBulkPricing=true) pueden pagar. Los demás
                    // (típicamente Transferencia) quedan bloqueados con aviso.
                    const cartHasTier = cart.some(itemHasOwnDiscount);
                    const acceptsBulk = m.acceptsBulkPricing === true;
                    const isDisabledByBulk = cartHasTier && !acceptsBulk;

                    const isDisabled = isDisabledByMin || isDisabledByBulk;
                    const helperText = isDisabledByBulk
                      ? '⚠️ Tenés productos con descuento por cantidad. Esos precios son EXCLUSIVOS para efectivo — con este método no podés pagarlos.'
                      : isDisabledByMin
                      ? `Aplica desde $${minAmt.toLocaleString('es-AR')}. Te faltan $${missing.toLocaleString('es-AR')} en el carrito para poder elegirla.`
                      : undefined;
                    return (
                      <RadioOption key={m.key} name='paymentMethod' value={m.key}
                        checked={formData.paymentMethod === m.key} onChange={handleChange}
                        label={m.restricted ? `🔒 ${lbl}` : lbl}
                        theme={theme}
                        disabled={isDisabled}
                        helperText={helperText}
                        testId={`payment-method-${m.key}`} />
                    );
                  });
                })()}
              </div>
              {_blocksVolumeDiscounts && formData.paymentMethod && (
                <div className="mt-2 px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2" data-testid="pm-no-volume-notice">
                  <AlertCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-900 leading-snug">
                    <b>Sin descuentos por volumen.</b> Al elegir &ldquo;{_selectedPaymentMethod?.label}&rdquo; los precios se muestran base y se ignoran los descuentos por objetivo de carrito, tiers por cantidad y escalones por total. El descuento permanente del cliente (si tiene) sigue aplicándose.
                  </p>
                </div>
              )}
            </FormField>
            {/* CUIT del checkout: ya no se pide. Se usa el del perfil del usuario. */}
            
            {/* Banner de error de submit (mínimo no alcanzado, errores de backend, etc.) */}
            {submitError && (
              <div className='p-4 bg-red-50 border-2 border-red-300 rounded-xl flex items-start gap-3 shadow-md animate-pulse-once' role='alert' data-testid='cart-submit-error'>
                <AlertCircle size={20} className='text-red-600 flex-shrink-0 mt-0.5' />
                <div className='flex-1'>
                  <p className='font-bold text-red-700 text-sm'>No se pudo enviar el pedido</p>
                  <p className='text-sm text-red-600 mt-0.5 whitespace-pre-line'>{submitError}</p>
                </div>
                <button type='button' onClick={() => setSubmitError('')} className='text-red-400 hover:text-red-600 text-xs' aria-label='Cerrar'>✕</button>
              </div>
            )}
            {/* Retail warnings */}
            {isRetail && !meetsMinimumPurchase() && (
              <div className='p-3 bg-slate-50 rounded-lg flex items-start gap-2 text-sm'>
                <AlertCircle size={16} className='text-slate-400 flex-shrink-0 mt-0.5' />
                <div>
                  <p className='font-medium text-slate-600'>Mínimo no alcanzado</p>
                  <p className='text-xs text-slate-500'>Pedido mínimo: {formatPrice(storeSettings?.retailMinimumPurchase || 50000)}. Carrito: {formatPrice(getCartTotal())}.</p>
                </div>
              </div>
            )}
            {isRetail && storeSettings?.retailWholesaleThreshold > 0 && (
              <div className='p-3 bg-slate-50 rounded-lg text-sm'>
                {getsWholesalePrices() ? (
                  <p className='text-emerald-600 font-medium'>Tenés precios mayoristas.</p>
                ) : (
                  <p className='text-slate-600'>Sumando ${(storeSettings.retailWholesaleThreshold - getRawCartTotal()).toLocaleString('es-AR')} más accedés a precios mayoristas.</p>
                )}
              </div>
            )}

            {/* Shipping Methods */}
            <FormField label="Método de Envío">
              <div className='space-y-2'>
                {shippingSettings.pickupEnabled && (
                  <ShippingOption name='shippingMethod' value='pickup' checked={formData.shippingMethod === 'pickup'} onChange={handleChange} icon={<MapPin size={18} />} label='Retiro en Depósito' badge='GRATIS' badgeColor='text-emerald-600' theme={theme} />
                )}
                {!_forcesPickup && shippingSettings.fastShippingEnabled && (
                  <ShippingOption name='shippingMethod' value='fast' checked={formData.shippingMethod === 'fast'} onChange={handleChange} icon={<Zap size={18} />} label='Envío Rápido' badge={`+${formatPrice(shippingSettings.fastShippingCost)}`} badgeColor='text-slate-600' theme={theme} />
                )}
                {!_forcesPickup && shippingSettings.deliveryEnabled && isDeliveryAvailable() && (
                  <ShippingOption name='shippingMethod' value='delivery' checked={formData.shippingMethod === 'delivery'} onChange={handleChange} icon={<Truck size={18} />} label='Reparto' badge='GRATIS' badgeColor='text-emerald-600' theme={theme} />
                )}
                {_forcesPickup && (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="forces-pickup-note">
                    📦 Como elegiste <b>{_selectedPaymentMethod?.label}</b>, solo podés retirar tu pedido en el depósito.
                  </p>
                )}
                {formData.shippingMethod === 'delivery' && (() => {
                  const dayMap = { domingo: 0, lunes: 1, martes: 2, miercoles: 3, 'miércoles': 3, jueves: 4, viernes: 5, sabado: 6, 'sábado': 6 };
                  const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
                  const today = new Date().getDay();
                  
                  const userCity = normalizeCity(isSellerMode ? (formData.sellerClientCity || formData.city) : formData.city);
                  const schedules = shippingSettings.deliverySchedules || [];
                  
                  // Find matching city schedule (new format: {city, days:[], timeFrom, timeTo} or old format: {city, day, time})
                  const citySchedule = schedules.find(s => {
                    const sc = normalizeCity(s.city);
                    return userCity === sc || (userCity.length > 3 && sc.length > 3 && (userCity.includes(sc) || sc.includes(userCity)));
                  });
                  
                  if (!citySchedule) return null;
                  
                  // Build delivery day numbers
                  let deliveryDayNums = [];
                  if (citySchedule.days?.length > 0) {
                    // New format: multiple days per city
                    deliveryDayNums = citySchedule.days.map(d => dayMap[d.toLowerCase()]).filter(n => n !== undefined);
                  } else if (citySchedule.day) {
                    // Old format: single day per entry
                    const dn = dayMap[(citySchedule.day || '').toLowerCase()];
                    if (dn !== undefined) deliveryDayNums = [dn];
                  }
                  
                  if (deliveryDayNums.length === 0) return null;
                  
                  // Find next delivery day from tomorrow
                  let nextDay = null;
                  for (let i = 1; i <= 7; i++) {
                    const check = (today + i) % 7;
                    if (deliveryDayNums.includes(check)) { nextDay = check; break; }
                  }
                  
                  if (nextDay === null) return null;
                  
                  // Build friendly time display from day-specific schedule
                  let timeNote = '';
                  const nextDayName = Object.entries(dayMap).find(([k, v]) => v === nextDay)?.[0] || '';
                  const dayScheds = citySchedule.daySchedules || [];
                  const dayMatch = dayScheds.find(ds => (ds.day || '').toLowerCase() === nextDayName);
                  
                  if (dayMatch && dayMatch.timeFrom && dayMatch.timeTo) {
                    const fmtH = (t) => (t || '').replace(':00', '').replace(/^0/, '');
                    timeNote = ` entre ${fmtH(dayMatch.timeFrom)} y ${fmtH(dayMatch.timeTo)}hs`;
                  } else if (citySchedule.timeFrom && citySchedule.timeTo) {
                    // Fallback: city-level time
                    const fmtH = (t) => (t || '').replace(':00', '').replace(/^0/, '');
                    timeNote = ` entre ${fmtH(citySchedule.timeFrom)} y ${fmtH(citySchedule.timeTo)}hs`;
                  } else if (citySchedule.time && citySchedule.time !== 'mañana' && citySchedule.time !== 'tarde') {
                    timeNote = ` ${citySchedule.time}`;
                  } else if (citySchedule.time) {
                    timeNote = ` por la ${citySchedule.time}`;
                  }
                  
                  return <p className='text-sm text-slate-500 pl-2'>Próximo reparto{userCity ? ` en ${formData.city || formData.sellerClientCity}` : ''}: <strong>{dayNames[nextDay]}{timeNote}</strong></p>;
                })()}
                {shippingSettings.customMethods?.filter(m => m.enabled).map(method => (
                  <ShippingOption key={method.id} name='shippingMethod' value={`custom_${method.id}`} checked={formData.shippingMethod === `custom_${method.id}`} onChange={handleChange} icon={<Truck size={18} />} label={method.name} badge={method.cost > 0 ? `+${formatPrice(method.cost)}` : 'GRATIS'} badgeColor={method.cost > 0 ? 'text-slate-600' : 'text-emerald-600'} theme={theme} description={method.description} />
                ))}
                {shippingSettings.deliveryEnabled && !isDeliveryAvailable() && formData.city && (
                  <p className='text-xs text-slate-400 pl-1'>Reparto gratuito disponible en: {shippingSettings.deliveryCities.join(', ')}</p>
                )}
              </div>
            </FormField>

            {/* Totals */}
            <div className='pt-4 border-t border-slate-100 space-y-2'>
              <div className='flex justify-between text-sm'>
                <span className='text-slate-500'>Subtotal</span>
                <span className='font-medium text-slate-700' data-testid='checkout-subtotal-value'>
                  {(() => {
                    // Bruto real (precio de lista × cantidad, sin descuentos).
                    const bruto = cart.reduce((sum, it) => {
                      return sum + Number(it.priceNumeric || 0) * (it.quantity || 0);
                    }, 0);
                    return formatPrice(bruto);
                  })()}
                </span>
              </div>
              {(() => {
                // Descuento por objetivo de carrito (winner-takes-all vs permanente).
                // Si el método bloquea descuentos por volumen, NO mostramos el 🎯.
                if (couponIsSingle()) return null;  // cupón único: no se muestran otros descuentos
                const rawTot = getRawCartTotal();
                const cartPct = (!_blocksVolumeDiscounts && getBestCartDiscountPct) ? Number(getBestCartDiscountPct(rawTot) || 0) : 0;
                const permPct = Number(user?.permanentDiscountPct || 0);
                const bruto = cart.reduce((sum, it) => sum + Number(it.priceNumeric || 0) * (it.quantity || 0), 0);
                const eligibleBruto = cart.reduce((sum, it) => {
                  if (it.isComboItem) return sum;
                  if (it.excludedFromCartDiscount) return sum;
                  return sum + Number(it.priceNumeric || 0) * (it.quantity || 0);
                }, 0);
                if (cartPct > permPct && cartPct > 0) {
                  const amt = Math.round(eligibleBruto * cartPct / 100);
                  return (
                    <div className='flex justify-between text-sm' data-testid='cart-target-discount-row'>
                      <span className='text-orange-700'>🎯 Descuento por objetivo ({cartPct}%)</span>
                      <span className='font-medium text-orange-600'>-{formatPrice(amt)}</span>
                    </div>
                  );
                }
                if (permPct > 0) {
                  const amt = Math.round(bruto * permPct / 100);
                  return (
                    <div className='flex justify-between text-sm' data-testid='permanent-discount-row'>
                      <span className='text-emerald-700'>✨ Descuento permanente ({permPct}%)</span>
                      <span className='font-medium text-emerald-600'>-{formatPrice(amt)}</span>
                    </div>
                  );
                }
                return null;
              })()}
              {(() => {
                if (couponIsSingle()) return null;
                const tier = (!_blocksVolumeDiscounts) ? getApplicableVolumeTier?.() : null;
                const dAmount = (!_blocksVolumeDiscounts) ? (getVolumeDiscountAmount?.() || 0) : 0;
                if (!tier || dAmount <= 0) return null;
                return (
                  <div className='flex justify-between text-sm' data-testid='volume-discount-row'>
                    <span className='text-emerald-700'>Descuento por volumen ({tier.discountPercent}%)</span>
                    <span className='font-medium text-emerald-600'>-{formatPrice(dAmount)}</span>
                  </div>
                );
              })()}
              {(() => {
                if (couponIsSingle()) return null;
                const bonusAmt = getProfileBonusAmount?.(user) || 0;
                if (!bonusAmt) return null;
                return (
                  <div className='flex justify-between text-sm' data-testid='profile-bonus-row'>
                    <span className='text-violet-700'>Bonus perfil completo ({profileBonus?.discountPercent}%)</span>
                    <span className='font-medium text-violet-600'>-{formatPrice(bonusAmt)}</span>
                  </div>
                );
              })()}
              {(() => {
                const cAmt = getCouponDiscount();
                if (!cAmt) return null;
                return (
                  <div className='flex justify-between text-sm' data-testid='coupon-discount-row'>
                    <span className='text-emerald-700'>Cupón {coupon.applied.code} ({coupon.applied.discountPercent}%)</span>
                    <span className='font-medium text-emerald-600'>-{formatPrice(cAmt)}</span>
                  </div>
                );
              })()}
              {(() => {
                if (couponIsSingle()) return null;
                const gifts = (getGiftItems ? getGiftItems() : []) || [];
                const gs = getGiftSavings ? getGiftSavings() : 0;
                if (!gifts.length || gs <= 0) return null;
                return (
                  <div className='flex justify-between text-sm bg-gradient-to-r from-amber-50 to-orange-50 border border-orange-200 rounded-lg px-2 py-1.5' data-testid='gift-savings-row'>
                    <span className='text-orange-700 font-semibold flex items-center gap-1'>
                      🎁 Regalo{gifts.length > 1 ? 's' : ''} ({gifts.map(g => g.name).join(', ')})
                    </span>
                    <span className='font-bold text-orange-600'>-{formatPrice(gs)}</span>
                  </div>
                );
              })()}
              {/* Cupón input */}
              {!coupon.applied && (
                <div className='flex items-center gap-1 pt-1'>
                  <input value={coupon.code} onChange={(e) => setCoupon(c => ({ ...c, code: e.target.value, error: '' }))}
                    placeholder='Código de cupón'
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyCoupon(); } }}
                    className='flex-1 px-2 py-1 border border-zinc-200 rounded text-xs uppercase' data-testid='coupon-input' />
                  <button type='button' onClick={applyCoupon}
                    className='px-2 py-1 bg-zinc-900 text-white rounded text-xs font-bold' data-testid='coupon-apply'>
                    Aplicar
                  </button>
                </div>
              )}
              {coupon.applied && (
                <div className='text-[11px] text-emerald-700 flex items-center justify-between'>
                  <span>✓ Cupón {coupon.applied.code} aplicado</span>
                  <button onClick={() => setCoupon({ code: '', applied: null, error: '' })} className='underline'>Quitar</button>
                </div>
              )}
              {coupon.error && <p className='text-[11px] text-rose-600' data-testid='coupon-error'>{coupon.error}</p>}
              {user && profileBonus?.enabled && !user.profileBonusUsed && !isProfileComplete?.(user) && (
                <div className='text-[11px] bg-violet-50 border border-violet-200 rounded p-2 text-violet-800' data-testid='profile-incomplete-hint'>
                  💡 Completá tu perfil (CUIT, condición IVA, ciudad) y obtené <strong>{profileBonus.discountPercent}% de descuento</strong> en este pedido.
                </div>
              )}
              {(() => {
                // 💳 Ajuste por forma de pago (Efectivo -2%, Transferencia 0%, etc.).
                if (couponIsSingle()) return null;  // cupón único: sin ajuste por forma de pago
                const pmSel = (storeSettings?.paymentMethods || []).find(m => m.key === formData.paymentMethod);
                const pmAdj = Number(pmSel?.adjustPercent || 0);
                if (pmAdj === 0) return null;
                const pmAmount = cart.reduce((sum, it) => {
                  if (itemHasOwnDiscount(it)) return sum;
                  const unit = getItemPrice(it);
                  const adjusted = Math.ceil(unit * (1 + pmAdj / 100));
                  return sum + (adjusted - unit) * (it.quantity || 0);
                }, 0);
                if (pmAmount === 0) return null;
                const label = pmSel?.label || pmSel?.name || 'Forma de pago';
                return (
                  <div className='flex justify-between text-sm' data-testid='payment-adjust-row'>
                    <span className={pmAmount > 0 ? 'text-slate-500' : 'text-emerald-700'}>
                      💳 {label} ({pmAdj > 0 ? '+' : ''}{pmAdj}%)
                    </span>
                    <span className={`font-medium ${pmAmount > 0 ? 'text-slate-700' : 'text-emerald-600'}`}>
                      {pmAmount > 0 ? '+' : '-'}{formatPrice(Math.abs(pmAmount))}
                    </span>
                  </div>
                );
              })()}
              {getShippingCost() > 0 && (
                <div className='flex justify-between text-sm'>
                  <span className='text-slate-500'>Envío</span>
                  <span className='font-medium text-slate-700'>+{formatPrice(getShippingCost())}</span>
                </div>
              )}
              {getShippingCost() === 0 && formData.shippingMethod && (
                <div className='flex justify-between text-sm'>
                  <span className='text-slate-500'>Envío</span>
                  <span className='font-medium text-emerald-600'>GRATIS</span>
                </div>
              )}
              <div className='flex justify-between pt-2 border-t border-slate-100'>
                <span className='font-bold text-slate-800'>Total</span>
                <span className='text-xl font-bold' style={{ fontFamily: 'Barlow Condensed', color: theme.primary }} data-testid='checkout-total-value'>{formatPrice(getFinalTotal())}</span>
              </div>
            </div>
            {renderNextTierHint('checkout')}
            <button type='submit' disabled={loading || !formData.shippingMethod} className='w-full text-white font-semibold py-3 rounded-xl disabled:opacity-40 transition-opacity hover:opacity-90' style={{ backgroundColor: theme.primary }} data-testid='submit-order-button'>
              {loading ? 'Procesando...' : 'Enviar Pedido por WhatsApp'}
            </button>
            <button type='button' onClick={() => setShowCheckout(false)} className='w-full py-2.5 text-sm text-slate-500 font-medium hover:text-slate-700 transition-colors'>Volver al carrito</button>
          </form>
          {/* Gate de encuesta: si hay una encuesta pendiente, aparece al apretar "Enviar Pedido"
              y se cierra antes de crear el pedido y abrir WhatsApp. */}
          <SurveyModal
            survey={pendingSurveyGate}
            onClose={() => {
              setPendingSurveyGate(null);
              // Reanudar el envío del pedido. Simulamos el evento de submit.
              setTimeout(() => {
                const form = document.querySelector('form[data-cart-checkout-form="true"]');
                if (form) form.requestSubmit();
              }, 0);
            }}
          />
          </>
        ) : (
          /* ── Cart Items ── */
          <>
            <div className='flex-1 overflow-y-auto px-5 py-3'>
              <div className='space-y-3'>
                {(() => {
                  // 🏪 Sólo renderizamos los headers de sucursal si al menos
                  // un item del carrito tiene branchName asignada. Un carrito
                  // sin sucursales (B2C, o comercio que todavía no eligió una)
                  // se ve plano como siempre.
                  const _hasAnyBranch = cart.some(it => !it.isGift && (it.branchName || '').trim());
                  if (!_hasAnyBranch) {
                    return cart.map(it => (
                      <React.Fragment key={it.id}>
                        {renderCartItem(it)}
                      </React.Fragment>
                    ));
                  }
                  // 🏪 Agrupamiento por sucursal: bucket por key lowercase,
                  // orden: sucursales (aparición) → __none__ → __gift__.
                  const buckets = new Map(); // key -> { branchName, isGift, items[] }
                  cart.forEach(it => {
                    const key = it.isGift
                      ? '__gift__'
                      : ((it.branchName || '').trim().toLowerCase() || '__none__');
                    if (!buckets.has(key)) {
                      buckets.set(key, { branchName: it.branchName || null, isGift: !!it.isGift, items: [] });
                    }
                    buckets.get(key).items.push(it);
                  });
                  const entries = Array.from(buckets.entries()).sort((a, b) => {
                    const pa = a[0] === '__gift__' ? 2 : a[0] === '__none__' ? 1 : 0;
                    const pb = b[0] === '__gift__' ? 2 : b[0] === '__none__' ? 1 : 0;
                    return pa - pb;
                  });
                  const rows = [];
                  entries.forEach(([key, bucket]) => {
                    if (!bucket.isGift) {
                      const subtotal = bucket.items.reduce(
                        (s, i) => s + (getItemPrice(i) || 0) * (i.quantity || 0),
                        0
                      );
                      rows.push(
                        <div
                          key={`branch-header-${key}`}
                          className={`flex items-center justify-between rounded-lg px-2.5 py-1.5 border ${
                            bucket.branchName
                              ? 'bg-orange-50 border-orange-200'
                              : 'bg-slate-100 border-slate-200'
                          }`}
                          data-testid={`cart-branch-header-${key}`}
                        >
                          <div className='flex items-center gap-1.5 min-w-0'>
                            <span className='text-[10px] font-black uppercase tracking-widest text-orange-600'>🏪 Sucursal</span>
                            <span className={`text-sm font-bold truncate ${bucket.branchName ? 'text-orange-900' : 'text-slate-500'}`} data-testid={`cart-branch-name-${key}`}>
                              {bucket.branchName || 'Sin asignar'}
                            </span>
                          </div>
                          <span className='text-[11px] text-slate-500 font-semibold whitespace-nowrap ml-2'>
                            {formatPrice(subtotal)}
                          </span>
                        </div>
                      );
                    }
                    bucket.items.forEach(it => {
                      rows.push(
                        <React.Fragment key={it.id}>
                          {renderCartItem(it)}
                        </React.Fragment>
                      );
                    });
                  });
                  return rows;
                })()}
              </div>

              {/* Suggestions */}
              {suggestions.length > 0 && (
                <div className='mt-4 pt-4 border-t border-slate-100'>
                  <p className='text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3'>Recomendados</p>
                  <div className='overflow-x-auto pb-2 -mx-1'>
                    <div className='flex gap-2 min-w-max px-1'>
                      {suggestions.map(product => {
                        const minQuantity = Math.max(1, Number(product.minQuantity) || 1);
                        const unitPrice = getPriceForQuantity(product, minQuantity);
                        return (
                          <div key={product.id} className='flex flex-col items-center gap-1.5 p-2 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors w-24 flex-shrink-0'>
                            <img src={encodeURI(product.imageUrl || '') + (product.imageUrl ? '?w=150' : '')} alt={product.name} className='w-14 h-14 object-contain rounded-lg bg-white' loading="lazy" />
                            <h4 className='text-[10px] font-medium text-slate-700 text-center leading-tight line-clamp-2 w-full' translate="no">{product.name}</h4>
                            <p className='text-xs font-bold' style={{ color: theme.primary }}>{formatPrice(unitPrice)}</p>
                            <button onClick={() => addToCart(product, minQuantity)} className='w-full py-1 rounded-lg text-white text-[10px] font-bold transition-opacity hover:opacity-90' style={{ backgroundColor: theme.primary }}>
                              <Plus size={12} className="inline" /> Agregar
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className='border-t border-slate-100 p-5 space-y-3'>
              {/* Subtotal (precio de lista) — matchea 1:1 con el monto que
                  muestra la barra de descuentos en el catálogo. Sólo se
                  muestra si difiere del Total (p.ej. cuando hay descuentos
                  por cantidad, retail surcharge o cart-tier aplicado). */}
              {(() => {
                const listSubtotal = typeof getRawCartTotal === 'function' ? Math.round(getRawCartTotal() || 0) : 0;
                const currentTotal = Math.round(getCartTotal() || 0);
                if (listSubtotal <= 0 || listSubtotal === currentTotal) return null;
                return (
                  <div className='flex justify-between items-baseline'>
                    <span className='text-xs text-slate-400'>Subtotal (precio de lista)</span>
                    <span className='text-sm font-semibold text-slate-500 line-through' data-testid='cart-list-subtotal'>{formatPrice(listSubtotal)}</span>
                  </div>
                );
              })()}
              <div className='flex justify-between items-baseline'>
                <span className='text-sm text-slate-500'>Total</span>
                <span className='text-2xl font-bold' style={{ fontFamily: 'Barlow Condensed', color: theme.primary }} data-testid='cart-total'>{formatPrice(getCartTotal())}</span>
              </div>
              {renderNextTierHint('cart')}
              <button onClick={() => setShowCheckout(true)} className='w-full text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 transition-opacity hover:opacity-90' style={{ backgroundColor: theme.primary }} data-testid='cart-checkout-button'>
                Finalizar Compra
                <ChevronRight size={18} />
              </button>
              <button onClick={() => { setIsCartOpen(false); navigate('/products'); }}
                className='w-full py-2.5 text-sm font-bold rounded-xl flex items-center justify-center gap-2 border-2 transition-colors hover:bg-slate-50'
                style={{ borderColor: theme.primary, color: theme.primary }}
                data-testid='cart-continue-shopping-button'>
                ← Seguir comprando
              </button>
              {confirmClear ? (
                <div className='flex gap-2'>
                  <button onClick={() => { clearCart(); setConfirmClear(false); }} className='flex-1 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors' data-testid='cart-clear-confirm'>
                    Sí, vaciar
                  </button>
                  <button onClick={() => setConfirmClear(false)} className='flex-1 py-2 text-sm font-medium text-slate-500 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors' data-testid='cart-clear-cancel'>
                    Cancelar
                  </button>
                </div>
              ) : (
                <button onClick={() => setConfirmClear(true)} className='w-full py-2 text-sm text-slate-400 font-medium hover:text-slate-600 transition-colors flex items-center justify-center gap-1' data-testid='cart-clear-button'>
                  <Trash2 size={14} />
                  Vaciar carrito
                </button>
              )}
              {referralCode && (
                <button onClick={() => {
                  const base = window.location.origin;
                  const url = `${base}?ref=${referralCode}`;
                  const msg = `Pedí bebidas en *Bebida Bacana* con envío y los mejores precios.\nUsá mi código *${referralCode}* y ganá puntos.\n${url}`;
                  if (navigator.share) {
                    navigator.share({ title: 'Bebida Bacana', text: msg }).catch(() => {});
                  } else {
                    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                  }
                }}
                  className='w-full py-3 rounded-xl font-medium text-sm transition-all hover:shadow-md active:scale-[0.98] flex items-center justify-center gap-2'
                  style={{ background: 'linear-gradient(135deg, #10B981, #059669)', color: 'white' }}
                  data-testid='share-app-cart'>
                  <Share2 size={16} style={{ animation: 'pulse 2s ease-in-out infinite' }} />
                  <span>Invitá amigos y ganá puntos</span>
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

// ── Helper Components ──
const FormField = ({ label, required, children }) => (
  <div>
    <label className='block text-xs font-medium text-slate-500 mb-1.5'>{label} {required && '*'}</label>
    {children}
  </div>
);

const RadioOption = ({ name, value, checked, onChange, label, theme, disabled = false, helperText, testId }) => (
  <label
    className={`flex items-start p-3 border rounded-xl transition-all ${
      disabled ? 'border-slate-200 bg-slate-50/60 opacity-70 cursor-not-allowed'
      : checked ? 'border-slate-400 bg-slate-50 cursor-pointer'
      : 'border-slate-200 hover:border-slate-300 cursor-pointer'
    }`}
    data-testid={testId}>
    <input type='radio' name={name} value={value} checked={checked && !disabled}
      onChange={disabled ? undefined : onChange}
      disabled={disabled}
      className='w-4 h-4 mt-0.5' style={{ accentColor: theme.primary }} />
    <div className='ml-3 flex-1'>
      <span className={`text-sm font-medium ${disabled ? 'text-slate-500' : 'text-slate-700'}`}>{label}</span>
      {helperText && (
        <p className='text-[11px] text-amber-600 mt-0.5 font-medium' data-testid={`${testId}-helper`}>{helperText}</p>
      )}
    </div>
  </label>
);

const ShippingOption = ({ name, value, checked, onChange, icon, label, badge, badgeColor, theme, description }) => (
  <label className={`flex items-center p-3 border rounded-xl cursor-pointer transition-all ${checked ? 'border-slate-400 bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}>
    <input type='radio' name={name} value={value} checked={checked} onChange={onChange} className='w-4 h-4' style={{ accentColor: theme.primary }} />
    <span className='ml-3 text-slate-400'>{icon}</span>
    <div className='ml-2 flex-1'>
      <div className='flex items-center gap-2'>
        <span className='text-sm font-medium text-slate-700'>{label}</span>
        <span className={`text-xs font-semibold ${badgeColor}`}>{badge}</span>
      </div>
      {description && <p className='text-xs text-slate-400'>{description}</p>}
    </div>
  </label>
);

export default CartSidebar;
